import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { fetchCampaignImage } from "@/lib/campaign-image-request";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import {
  AlertTriangle,
  Lock,
  Unlock,
  RefreshCw,
  Pause,
  Play,
  CheckCircle,
  XCircle,
  Loader2,
  Clock,
  FileText,
  Zap,
  Eye,
  EyeOff,
  Brain,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Check,
  CheckCircle2,
  Image,
  Video,
  Layers,
  Target,
  ShoppingBag,
  ArrowRight,
  ArrowLeft,
  Wand2,
  Save,
  Download,
  Paintbrush,
  RotateCcw,
} from "lucide-react";
import type {
  CreativeBriefsState,
  BriefJob,
  CreativeBriefResult,
  AdUnitSelection,
  WizardAdUnit,
  NarrativeAngle,
  NarrativeOpportunity,
  CampaignPlanSummary,
  FunnelStrategyData,
  FunnelStageStrategy,
  PlannerPersona,
  CreativeStrategistPersona,
  AdUnitPlan,
  MediaAllocation,
  MediaMixConfig,
} from "@/lib/campaign-draft";
import type { Product, Persona, Brand } from "@shared/schema";
import { displayUrl, normalizeExampleImage } from "@shared/adImage";

function exampleImageUrlsCB(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((e) => displayUrl(normalizeExampleImage(e)))
    .filter((u): u is string => !!u);
}
import { buildCreativeBriefJobs, FUNNEL_STAGE_ORDER } from "@/lib/creative-briefs/buildCreativeBriefJobs";
import { buildCreativeBriefPayload } from "@/lib/creative-briefs/buildCreativeBriefPayload";
import { CreativeStrategistLoading } from "./creative-strategist-loading";
import { InpaintingModal } from "./inpainting-modal";
import {
  normalizeCreativeBriefResponse,
  validateCreativeBrief,
} from "@/lib/creative-briefs/normalizeCreativeBriefResponse";

interface StageAiSelectedUnit {
  ad_unit_name: string;
  quantity: number;
  selection_reason: string;
  role_in_stage: string;
  best_fit_angles?: string[];
  performance_confidence?: string;
}

interface StageAiResult {
  executive_summary: string;
  selected_ad_units: StageAiSelectedUnit[];
}

interface AngleAssignedUnit {
  ad_concept_name: string;
  ad_unit: string;
  diversification_role: string;
  hook_direction: string;
  idea_summary: string;
  primary_persuasion_type: string;
  selected_angle: string;
  tone: string;
  why_this_fit: string;
}

interface AngleAssignmentResult {
  ad_unit_selections: AngleAssignedUnit[];
  selection_summary?: {
    selection_logic?: string;
    stage_objective?: string;
    diversification_logic?: string;
    dominant_angles?: string[];
    supporting_angles?: string[];
  };
  funnel_stage?: string;
}

interface CreativeBriefsStepProps {
  creativeBriefs: CreativeBriefsState;
  product: Product | null;
  // Task #744 — chosen variant name (null = "no specific variant").
  selectedVariantName: string | null;
  persona: Persona | null;
  brand: Brand | null;
  funnelStrategy: FunnelStrategyData | null;
  campaignPlan: CampaignPlanSummary | null;
  plannerPersona: PlannerPersona | null;
  creativeStrategist: CreativeStrategistPersona | null;
  adUnitSelections: AdUnitSelection[];
  adUnitPlan: AdUnitPlan | null;
  narrativeAngles: NarrativeAngle[];
  narrativeOpportunity: NarrativeOpportunity | null;
  mediaAllocation: MediaAllocation | null;
  mediaMix: MediaMixConfig | null;
  onUpdate: (state: CreativeBriefsState) => void;
  onUpdateCreativeStrategist: (strategist: CreativeStrategistPersona | null) => void;
  onUpdateAdUnitSelections: (selections: AdUnitSelection[]) => void;
  onUpdateMediaAllocation: (allocation: MediaAllocation | null) => void;
  isStale: boolean;
  onClearStale: () => void;
  draftId: string;
  draftName: string;
  onRegisterSave?: (handler: { save: () => Promise<void>; canSave: boolean; isSaving: boolean }) => void;
  onResetDraft?: () => void;
}

const LIVE_STATUS_MESSAGES = [
  "Loading stage context",
  "Adapting angle to ad unit",
  "Drafting hook directions",
  "Building visual structure",
  "Writing copy direction",
  "Crafting headline options",
  "Finalizing brief",
];

function computeCounts(queue: BriefJob[] | undefined | null) {
  const q = Array.isArray(queue) ? queue : [];
  return {
    completed: q.filter((j) => j.status === "completed" || j.status === "locked").length,
    failed: q.filter((j) => j.status === "failed").length,
    running: q.filter((j) => j.status === "running").length,
    pending: q.filter((j) => j.status === "pending").length,
    locked: q.filter((j) => j.status === "locked").length,
    total: q.length,
  };
}

function groupByStage(queue: BriefJob[] | undefined | null): Map<string, BriefJob[]> {
  const q = Array.isArray(queue) ? queue : [];
  const map = new Map<string, BriefJob[]>();
  for (const stage of FUNNEL_STAGE_ORDER) {
    const jobs = q.filter((j) => j.stage === stage);
    if (jobs.length > 0) map.set(stage, jobs);
  }
  const seen = new Set(FUNNEL_STAGE_ORDER);
  for (const job of q) {
    if (!seen.has(job.stage)) {
      seen.add(job.stage);
      map.set(job.stage, q.filter((j) => j.stage === job.stage));
    }
  }
  return map;
}

function JobStatusIcon({ status }: { status: BriefJob["status"] }) {
  if (status === "completed" || status === "locked") return <CheckCircle className="w-4 h-4 text-emerald-500" />;
  if (status === "failed") return <XCircle className="w-4 h-4 text-red-500" />;
  if (status === "running") return <Loader2 className="w-4 h-4 text-violet-500 animate-spin" />;
  return <Clock className="w-4 h-4 text-muted-foreground" />;
}

function BriefField({ label, value }: { label: string; value: string | string[] }) {
  if (!value || (Array.isArray(value) && value.length === 0) || value === "") return null;
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
      {Array.isArray(value) ? (
        <ul className="space-y-1">
          {value.map((v, i) => (
            <li key={i} className="text-sm text-foreground flex gap-2">
              <span className="text-muted-foreground mt-0.5 flex-shrink-0">—</span>
              <span>{v}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-foreground leading-relaxed">{value}</p>
      )}
    </div>
  );
}

function BriefCard({
  job,
  onRegenerate,
  onRetry,
  onToggleLock,
  onGenerateImage,
  onUpdateImageUrl,
  isGenerating,
  referenceImageUrls,
  brandId,
}: {
  job: BriefJob;
  onRegenerate: (jobId: string) => void;
  onRetry: (jobId: string) => void;
  onToggleLock: (jobId: string) => void;
  onGenerateImage: (jobId: string) => void;
  onUpdateImageUrl: (jobId: string, newUrl: string) => void;
  isGenerating: boolean;
  referenceImageUrls?: string[];
  brandId?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [imageExpanded, setImageExpanded] = useState(false);
  const [inpaintOpen, setInpaintOpen] = useState(false);
  const brief = job.brief as CreativeBriefResult | null;

  const borderClass =
    job.status === "completed" || job.status === "locked"
      ? "border-emerald-500/20 bg-emerald-500/5"
      : job.status === "failed"
        ? "border-red-500/20 bg-red-500/5"
        : job.status === "running"
          ? "border-violet-500/20 bg-violet-500/5"
          : "border-border bg-background";

  return (
    <Card className={`border ${borderClass} transition-all duration-200`} data-testid={`brief-card-${job.jobId}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <JobStatusIcon status={job.status} />

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">{job.adUnitName}</p>
          {job.angleName && (
            <p className="text-xs text-muted-foreground truncate">{job.angleName}</p>
          )}
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <Badge variant="outline" className="text-xs capitalize hidden sm:flex">
            {job.format}
          </Badge>

          {job.status === "failed" && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => onRetry(job.jobId)}
              disabled={isGenerating}
              data-testid={`btn-retry-${job.jobId}`}
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Retry
            </Button>
          )}

          {(job.status === "completed" || job.status === "locked") && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => onRegenerate(job.jobId)}
                disabled={isGenerating || job.status === "locked"}
                data-testid={`btn-regenerate-${job.jobId}`}
              >
                <RefreshCw className="w-3 h-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => onToggleLock(job.jobId)}
                data-testid={`btn-lock-${job.jobId}`}
              >
                {job.status === "locked" ? (
                  <Unlock className="w-3 h-3" />
                ) : (
                  <Lock className="w-3 h-3" />
                )}
              </Button>
            </>
          )}

          {brief && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => setExpanded((e) => !e)}
              data-testid={`btn-expand-${job.jobId}`}
            >
              {expanded ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            </Button>
          )}
        </div>
      </div>

      {job.format === "static" && (job.status === "completed" || job.status === "locked") && (
        <div className="px-4 pb-3">
          {job.imageStatus === "generating" && (
            <div className="flex items-center gap-2 rounded-xl bg-violet-500/5 border border-violet-500/20 px-3 py-2.5" data-testid={`image-generating-${job.jobId}`}>
              <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-500" />
              <span className="text-xs text-violet-600 dark:text-violet-400">Generating ad image...</span>
            </div>
          )}
          {job.imageStatus === "completed" && job.generatedImageUrl && (
            <div className="space-y-2" data-testid={`image-result-${job.jobId}`}>
              <button
                className="w-full cursor-pointer rounded-xl overflow-hidden border border-border hover:border-violet-500/40 transition-all"
                onClick={() => setImageExpanded((v) => !v)}
                data-testid={`btn-image-toggle-${job.jobId}`}
              >
                <img
                  src={job.generatedImageUrl}
                  alt={`Generated ad for ${job.adUnitName}`}
                  className={`w-full object-contain ${imageExpanded ? "max-h-[600px]" : "max-h-[200px]"}`}
                />
              </button>
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Generated Image</span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[10px]"
                    onClick={() => setInpaintOpen(true)}
                    data-testid={`btn-edit-image-${job.jobId}`}
                  >
                    <Paintbrush className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[10px]"
                    onClick={() => onGenerateImage(job.jobId)}
                    disabled={String(job.imageStatus) === "generating"}
                    data-testid={`btn-regenerate-image-${job.jobId}`}
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Regenerate
                  </Button>
                </div>
              </div>
              <InpaintingModal
                open={inpaintOpen}
                onClose={() => setInpaintOpen(false)}
                imageUrl={job.generatedImageUrl!}
                onAccept={(newUrl) => onUpdateImageUrl(job.jobId, newUrl)}
                referenceImageUrls={referenceImageUrls}
                brandId={brandId}
              />
            </div>
          )}
          {job.imageStatus === "failed" && (
            <div className="flex items-center justify-between rounded-xl bg-red-500/5 border border-red-500/20 px-3 py-2" data-testid={`image-failed-${job.jobId}`}>
              <span className="text-xs text-red-500 truncate flex-1 mr-2">{job.imageError || "Image generation failed"}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-red-500 hover:text-red-600 flex-shrink-0"
                onClick={() => onGenerateImage(job.jobId)}
                data-testid={`btn-retry-image-${job.jobId}`}
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Retry
              </Button>
            </div>
          )}
          {job.imageStatus === "idle" && (
            <Button
              variant="outline"
              size="sm"
              className="w-full h-8 text-xs border-dashed"
              onClick={() => onGenerateImage(job.jobId)}
              data-testid={`btn-generate-image-${job.jobId}`}
            >
              <Image className="w-3.5 h-3.5 mr-1.5" />
              Generate Ad Image
            </Button>
          )}
        </div>
      )}

      {job.status === "failed" && job.error && (
        <div className="px-4 pb-3">
          <p className="text-xs text-red-500 bg-red-500/5 rounded-lg px-3 py-2">{job.error}</p>
        </div>
      )}

      {brief && expanded && (
        <div className="px-4 pb-5 space-y-4 border-t border-border pt-4">
          <BriefField label="Objective" value={brief.objective} />
          <BriefField label="Hook Direction" value={brief.hook_direction} />
          <BriefField label="Headline Options" value={brief.headline_options} />
          <BriefField label="Copy Direction" value={brief.copy_direction} />
          <BriefField label="Visual Direction" value={brief.visual_direction} />
          <BriefField label="Structure Template" value={brief.structure_template} />
          <BriefField label="Proof Elements" value={brief.proof_elements} />
          <BriefField label="CTA Direction" value={brief.cta_direction} />
          {brief.designer_notes && <BriefField label="Designer Notes" value={brief.designer_notes} />}
          {brief.ai_generation_prompt && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                AI Generation Prompt
              </p>
              <p className="text-xs text-muted-foreground font-mono bg-muted/50 rounded-lg p-3 leading-relaxed">
                {brief.ai_generation_prompt}
              </p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function LiveGenerationPanel({
  job,
  statusMessage,
}: {
  job: BriefJob | null;
  statusMessage: string;
}) {
  if (!job) return null;
  return (
    <Card className="border border-violet-500/30 bg-violet-500/5">
      <div className="flex items-start gap-4 px-5 py-4">
        <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Zap className="w-4 h-4 text-violet-400 animate-pulse" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-violet-400 mb-1">
            Generating Brief
          </p>
          <div className="flex flex-wrap gap-x-2 gap-y-0.5 mb-2">
            <span className="text-sm font-semibold text-foreground">{job.stage}</span>
            <span className="text-sm text-muted-foreground">/</span>
            <span className="text-sm text-foreground">{job.adUnitName}</span>
            {job.angleName && (
              <>
                <span className="text-sm text-muted-foreground">/</span>
                <span className="text-sm text-muted-foreground italic">{job.angleName}</span>
              </>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{statusMessage}</p>
        </div>
        <Loader2 className="w-5 h-5 text-violet-400 animate-spin flex-shrink-0" />
      </div>
    </Card>
  );
}

// ─── Creative Strategist Section ──────────────────────────────────────────────

function CreativeStrategistSection({
  strategist,
  isGenerating,
  error,
  onGenerate,
  onRegenerate,
}: {
  strategist: CreativeStrategistPersona | null;
  isGenerating: boolean;
  error: string | null;
  onGenerate: () => void;
  onRegenerate: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  if (isGenerating) {
    return <CreativeStrategistLoading />;
  }

  if (!strategist) {
    return (
      <Card className="rounded-2xl border-dashed" data-testid="card-strategist-empty">
        <CardContent className="flex flex-col items-center justify-center py-12 text-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-primary/[0.07] border border-primary/15 flex items-center justify-center">
            <Brain className="w-6 h-6 text-primary/60" />
          </div>
          <div>
            <h3 className="font-semibold text-base mb-1">Create Your AI Creative Strategist</h3>
            <p className="text-sm text-muted-foreground max-w-xs">
              Generate a bespoke AI creative strategist calibrated to your campaign plan, persona, and narrative angles.
            </p>
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <Button onClick={onGenerate} className="rounded-full gap-2" data-testid="btn-generate-strategist">
            <Sparkles className="h-4 w-4" />
            Generate Creative Strategist
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-0 rounded-2xl overflow-hidden border border-primary/25" data-testid="card-creative-strategist">
      {/* Header gradient banner */}
      <div
        className="relative px-5 py-4 text-white overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 35%, #0f2a4a 65%, #1d4ed8 100%)",
          backgroundSize: "250% 250%",
          animation: "gradient-shift 10s ease infinite",
        }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.12) 1px, transparent 1px)",
            backgroundSize: "18px 18px",
          }}
        />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute top-0 bottom-0 w-8"
            style={{
              background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)",
              animation: "scan-shimmer 5s ease-in-out infinite",
              animationDelay: "2s",
            }}
          />
        </div>

        <div className="relative flex items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-cyan-400" />
              </span>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-blue-200">Your AI Creative Strategist</p>
            </div>
            <h3 className="text-xl font-bold tracking-tight" data-testid="text-strategist-name">{strategist.name}</h3>
            <div className="flex items-center gap-2 flex-wrap mt-1">
              <span className="text-xs font-semibold bg-white/15 text-white px-2.5 py-0.5 rounded-full border border-white/20">
                {strategist.archetype}
              </span>
              {strategist.personality_traits?.slice(0, 3).map((t) => (
                <span key={t} className="text-xs bg-white/10 text-blue-100 px-2 py-0.5 rounded-full border border-white/10">
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={onRegenerate}
              className="text-white/70 hover:text-white hover:bg-white/10 rounded-full h-7 px-3 text-xs gap-1"
              data-testid="btn-regenerate-strategist"
            >
              <RefreshCw className="h-3 w-3" />
              Regenerate
            </Button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="bg-card px-5 py-4 space-y-4">
        {/* Mission */}
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">Mission</p>
          <p className="text-sm leading-relaxed">{strategist.mission}</p>
        </div>

        {/* Signature strength */}
        {strategist.signature_strength && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-blue-500/5 border border-blue-500/15">
            <Zap className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-blue-500/70 mb-0.5">Signature Strength</p>
              <p className="text-sm">{strategist.signature_strength}</p>
            </div>
          </div>
        )}

        {/* Core beliefs */}
        {strategist.core_beliefs_about_creative_strategy?.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">Core Beliefs</p>
            <ul className="space-y-1.5">
              {strategist.core_beliefs_about_creative_strategy.slice(0, 3).map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-blue-400 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Expand toggle */}
        <button
          onClick={() => setExpanded((p) => !p)}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          data-testid="btn-strategist-expand"
        >
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {expanded ? "Show less" : "View full profile"}
        </button>

        {expanded && (
          <div className="space-y-4 pt-1 border-t border-border">
            {/* Creative philosophy */}
            {strategist.creative_philosophy && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">Creative Philosophy</p>
                <div className="grid gap-2">
                  {Object.entries(strategist.creative_philosophy).map(([k, v]) => (
                    <div key={k} className="p-3 rounded-xl bg-muted/40">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">
                        {k.replace(/_/g, " ")}
                      </p>
                      <p className="text-sm">{v}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Audience & Funnel instincts */}
            {strategist.audience_and_funnel_instincts && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">Audience &amp; Funnel Instincts</p>
                <div className="grid gap-2">
                  {Object.entries(strategist.audience_and_funnel_instincts).map(([k, v]) => (
                    <div key={k} className="p-3 rounded-xl bg-muted/40">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-blue-500/70 mb-0.5">
                        {k.replace(/_/g, " ")}
                      </p>
                      <p className="text-sm">{v}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Creative preferences */}
            {strategist.creative_preferences && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">Creative Preferences</p>
                <div className="grid gap-3">
                  {Object.entries(strategist.creative_preferences).map(([k, v]) => (
                    Array.isArray(v) && v.length > 0 ? (
                      <div key={k}>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                          {k.replace(/_/g, " ")}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {v.map((item: string) => (
                            <span key={item} className="text-xs bg-blue-500/10 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded-full border border-blue-500/20">
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null
                  ))}
                </div>
              </div>
            )}

            {/* Evaluation criteria */}
            {strategist.evaluation_criteria && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">Evaluation Criteria</p>
                {strategist.evaluation_criteria.how_this_strategist_judges_a_direction && (
                  <p className="text-sm mb-3">{strategist.evaluation_criteria.how_this_strategist_judges_a_direction}</p>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/15">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mb-1.5">Has Potential</p>
                    <ul className="space-y-1">
                      {strategist.evaluation_criteria.signs_a_direction_has_potential?.slice(0, 3).map((s, i) => (
                        <li key={i} className="text-xs flex items-start gap-1.5">
                          <span className="mt-1 w-1 h-1 rounded-full bg-emerald-400 shrink-0" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-3 rounded-xl bg-red-500/5 border border-red-500/15">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-red-600 dark:text-red-400 mb-1.5">Too Generic</p>
                    <ul className="space-y-1">
                      {strategist.evaluation_criteria.signs_a_direction_is_generic?.slice(0, 3).map((s, i) => (
                        <li key={i} className="text-xs flex items-start gap-1.5">
                          <span className="mt-1 w-1 h-1 rounded-full bg-red-400 shrink-0" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Angle interpretation */}
            {strategist.angle_interpretation && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">Angle Interpretation</p>
                <div className="grid gap-2">
                  {Object.entries(strategist.angle_interpretation).map(([k, v]) => (
                    <div key={k} className="p-3 rounded-xl bg-muted/40">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">
                        {k.replace(/_/g, " ")}
                      </p>
                      <p className="text-sm">{v as string}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Inline Ad Unit Stage Selector ────────────────────────────────────────────

// Rotating palette for N funnel stages (safe static class names)
const STAGE_PALETTE = [
  { headerBg: "bg-violet-500/[0.05] border-violet-500/15", iconBg: "bg-violet-500/12", iconColor: "text-violet-600 dark:text-violet-400", dotBg: "bg-violet-500" },
  { headerBg: "bg-blue-500/[0.05] border-blue-500/15",     iconBg: "bg-blue-500/12",   iconColor: "text-blue-600 dark:text-blue-400",   dotBg: "bg-blue-500" },
  { headerBg: "bg-pink-500/[0.05] border-pink-500/15",     iconBg: "bg-pink-500/12",   iconColor: "text-pink-600 dark:text-pink-400",   dotBg: "bg-pink-500" },
  { headerBg: "bg-amber-500/[0.05] border-amber-500/15",   iconBg: "bg-amber-500/12",  iconColor: "text-amber-600 dark:text-amber-400", dotBg: "bg-amber-500" },
  { headerBg: "bg-emerald-500/[0.05] border-emerald-500/15", iconBg: "bg-emerald-500/12", iconColor: "text-emerald-600 dark:text-emerald-400", dotBg: "bg-emerald-500" },
  { headerBg: "bg-orange-500/[0.05] border-orange-500/15", iconBg: "bg-orange-500/12", iconColor: "text-orange-600 dark:text-orange-400", dotBg: "bg-orange-500" },
  { headerBg: "bg-teal-500/[0.05] border-teal-500/15",     iconBg: "bg-teal-500/12",   iconColor: "text-teal-600 dark:text-teal-400",   dotBg: "bg-teal-500" },
];

// One icon per stage slot, cycling if there are more
const STAGE_ICONS = [Layers, Brain, Sparkles, Target, Zap, ShoppingBag, FileText];

function catalogToWizardUnit(raw: any): WizardAdUnit {
  return {
    id: raw.id,
    name: raw.name,
    format: raw.formatType === "video" ? "video" : "static",
    category: raw.formatType === "video" ? "Video" : "Static",
    description: raw.objective || raw.strategicRole?.slice(0, 120) || "",
    supported_funnel_stages: [(raw.funnelStage || "tof").toLowerCase()],
    recommended_use_case: raw.strategicRole?.slice(0, 200) || "",
    exampleImages: exampleImageUrlsCB(raw.exampleImages),
  };
}

// Map any funnel stage name to the catalog's tof/mof/bof group
function stageToCatalogGroup(stageName: string, stageGroup: string): string {
  // Try the stage_group field directly first
  const g = stageGroup.toLowerCase();
  if (g === "tof" || g === "mof" || g === "bof") return g;

  // Map by canonical stage name
  const n = stageName.toLowerCase();
  if (["awareness", "knowledge"].includes(n)) return "tof";
  if (["liking", "preference", "consideration", "interest"].includes(n)) return "mof";
  if (["conviction", "purchase", "action", "decision", "conversion"].includes(n)) return "bof";

  // Heuristic: first third → tof, last third → bof, middle → mof
  return "mof";
}

// Distribute a campaign-wide static-units budget evenly across N stages so
// the per-stage portions sum EXACTLY to the budget. Uses cumulative-floor
// allocation, which spreads the remainder across stages instead of rounding
// every stage up (which would overshoot the budget — e.g. 20 / 6 → ceil = 4
// per stage → 24 total). Returns 0 when the budget or stage count is zero.
function getStageBudgetPortion(
  budget: number,
  stageIndex: number,
  totalStages: number,
): number {
  if (!Number.isFinite(budget) || budget <= 0) return 0;
  if (!Number.isFinite(totalStages) || totalStages <= 0) return 0;
  if (stageIndex < 0 || stageIndex >= totalStages) return 0;
  const upper = Math.floor(((stageIndex + 1) * budget) / totalStages);
  const lower = Math.floor((stageIndex * budget) / totalStages);
  return Math.max(0, upper - lower);
}

// Resolve the per-stage static count from a Campaign Plan media allocation.
// The plan ALWAYS uses 6 canonical keys in this order; wizard funnel stages
// may use a different model (e.g. Lavidge), so we try name match first, then
// a positional match using stageIndex.
const PLAN_STAGE_ORDER = ["awareness", "interest", "consideration", "intent", "evaluation", "purchase"] as const;
function getPlanStaticForStage(
  mediaAllocation: any,
  stageName: string | undefined,
  stageGroup: string | undefined,
  stageIndex: number,
  budgetStaticUnits?: number | null,
): number {
  const planMap = mediaAllocation?.allocation_plan ?? null;
  if (!planMap) return 0;
  // Defensive guard: if the persisted allocation was generated against an
  // older, larger ad-unit count, the stage values can dwarf the user's
  // current budget (e.g. plan says 44 statics for a stage but the user only
  // has 5 statics total now). In that case the persisted allocation is
  // clearly stale and should be ignored entirely.
  if (typeof budgetStaticUnits === "number" && budgetStaticUnits > 0) {
    let totalPlanStatic = 0;
    for (const v of Object.values(planMap)) {
      const s = (v as any)?.ad_types?.static;
      if (typeof s === "number") totalPlanStatic += s;
    }
    if (totalPlanStatic > budgetStaticUnits) return 0;
  }
  const lowerName = (stageName ?? "").toLowerCase();
  const lowerGroup = (stageGroup ?? "").toLowerCase();
  let matchKey: string | undefined = Object.keys(planMap).find((k) => {
    const lk = k.toLowerCase();
    return lk === lowerName || lk === lowerGroup;
  });
  if (!matchKey && stageIndex >= 0 && stageIndex < PLAN_STAGE_ORDER.length) {
    const positional = PLAN_STAGE_ORDER[stageIndex];
    if (planMap[positional]) matchKey = positional;
  }
  if (!matchKey) return 0;
  const planStatic = planMap[matchKey]?.ad_types?.static;
  return typeof planStatic === "number" ? planStatic : 0;
}

function InlineAdUnitStageSelector({
  funnelStages,
  adUnitSelections,
  adUnitPlan,
  stageIndex,
  mediaAllocation,
  mediaMix,
  onGenerate,
  onAssignAngles,
  onSaveStage,
  onGenerateStageAssets,
  isGeneratingPlan,
  isAssigningAngles,
  isLocked,
  isSaved,
  aiResult,
  angleResult,
  stageImageCounts,
  stageImageJobs,
  availableFormatNames,
  onUpdateStageImageUrl,
  referenceImageUrls,
  brandId,
}: {
  funnelStages: FunnelStageStrategy[];
  adUnitSelections: AdUnitSelection[];
  adUnitPlan: AdUnitPlan | null;
  stageIndex: number;
  mediaAllocation: MediaAllocation | null;
  mediaMix: MediaMixConfig | null;
  onGenerate: () => void;
  onAssignAngles: () => void;
  onSaveStage: () => void;
  onGenerateStageAssets: () => void;
  isGeneratingPlan: boolean;
  isAssigningAngles: boolean;
  isLocked: boolean;
  isSaved: boolean;
  aiResult?: StageAiResult | null;
  angleResult?: AngleAssignmentResult | null;
  stageImageCounts?: { total: number; generating: number; completed: number; failed: number; idle: number } | null;
  stageImageJobs?: Array<{ id: string; adUnitName: string; adConceptName: string; status: "idle" | "generating" | "completed" | "failed"; imageUrl: string | null; error: string | null }> | null;
  availableFormatNames?: string[];
  onUpdateStageImageUrl?: (jobId: string, newUrl: string) => void;
  referenceImageUrls?: string[];
  brandId?: string;
}) {
  const [showStrategy, setShowStrategy] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [inpaintJobId, setInpaintJobId] = useState<string | null>(null);

  const stage = funnelStages[stageIndex];
  const stageName = stage?.stage ?? "";
  const colors = STAGE_PALETTE[stageIndex % STAGE_PALETTE.length];
  const StageIcon = STAGE_ICONS[stageIndex % STAGE_ICONS.length];

  // Map stage name → catalog group so we can match selections stored by the prior Ad Unit Selection step
  // (which stores funnelStageId as catalog group: "tof" | "mof" | "bof")
  const stageCatalogGroup = useMemo(
    () => stageToCatalogGroup(stageName, stage?.stage_group ?? ""),
    [stageName, stage]
  );

  // Selections for this specific stage (already chosen in the prior Ad Unit Selection step)
  // Match by catalog group ("tof"/"mof"/"bof") OR by stage name ("Awareness") for compatibility
  const stageSelections = useMemo(
    () => adUnitSelections.filter(
      (s) => s.funnelStageId === stageCatalogGroup || s.funnelStageId === stageName
    ),
    [adUnitSelections, stageCatalogGroup, stageName]
  );

  // Unique format names from the Ad Unit Plan for this stage (step 7 AI plan)
  // Used to supplement manual selections — the plan knows which formats are intended for this stage
  const planFormatNames = useMemo((): string[] => {
    const stagePlan = adUnitPlan?.stagePlans?.find(
      (sp) => sp.stage === stageName || sp.stage === stageCatalogGroup
    );
    if (!stagePlan?.ads?.length) return [];
    return Array.from(new Set(stagePlan.ads.map((a) => a.ad_unit_name)));
  }, [adUnitPlan, stageName, stageCatalogGroup]);

  // Format names that make up "the mix" for this stage. Priority order, so
  // the count matches what the user expects to see:
  //   1) After the AI generates a diversity mix → the formats the AI picked.
  //   2) Otherwise → the formats the user pre-selected for this stage on
  //      the Ad Unit Selection step.
  //   3) Otherwise → the formats from the upstream Ad Unit Plan.
  //   4) Otherwise → empty. The "no formats pre-selected" hint below
  //      already covers that case; we don't pretend the full catalog is
  //      "the mix".
  // The previous union-of-everything inflated the badge above the user's
  // actual selections (e.g. 14 picks → 24 because it added every catalog
  // format for the funnel stage on top).
  const displayFormatNames = useMemo((): string[] => {
    if (aiResult?.selected_ad_units?.length) {
      return Array.from(new Set(
        aiResult.selected_ad_units.map((u: any) => u.ad_unit_name ?? "")
      )).filter(Boolean);
    }
    const selectionNames = Array.from(new Set(
      stageSelections.map((s) => s.adUnit?.name ?? "")
    )).filter(Boolean);
    if (selectionNames.length) return selectionNames;
    if (planFormatNames.length) return planFormatNames;
    return [];
  }, [stageSelections, planFormatNames, aiResult]);

  const selectedCount = displayFormatNames.length;

  const stageStaticTarget = useMemo(() => {
    // What the badge means: how many static ads we plan to actually MAKE
    // for this stage. Must match what handleGeneratePlan and
    // handleFullPipeline use as ads_to_select, so the badge and the
    // generator never disagree.
    //
    // Priority (mirrors handleGeneratePlan / handleFullPipeline exactly):
    //   1) AI per-stage allocation from the Campaign Plan (already
    //      validated against the budget by getPlanStaticForStage).
    //   2) Per-stage portion of the user's static-units budget
    //      (mediaMix.staticUnits divided across funnel stages).
    //   3) null — no badge. (The generator uses default 3 in this case
    //      but we don't want to surface a number we have no signal for.)
    const planStatic = getPlanStaticForStage(
      mediaAllocation,
      stageName,
      stage?.stage_group,
      stageIndex,
      mediaMix?.staticUnits ?? null,
    );
    if (planStatic > 0) return planStatic;
    const wizardStaticUnits = mediaMix?.staticUnits ?? 0;
    if (wizardStaticUnits > 0) {
      const totalStages = Math.max(1, funnelStages.length);
      const perStage = getStageBudgetPortion(wizardStaticUnits, stageIndex, totalStages);
      if (perStage > 0) return perStage;
    }
    return null;
  }, [mediaAllocation, mediaMix, stageName, stage, stageIndex, funnelStages]);

  return (<>
    <Card className={`overflow-hidden border transition-opacity ${isLocked ? "opacity-40 pointer-events-none" : ""} ${isSaved ? "border-green-500/30" : ""}`} data-testid={`inline-stage-selector-${stageIndex}`}>

      {/* ── Stage header ── */}
      <div className={`px-5 pt-4 pb-3 border-b ${isLocked ? "bg-muted/30" : isSaved ? "bg-green-500/5" : colors.headerBg}`}>
        {/* Stage identity */}
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${isSaved ? "bg-green-500/10" : colors.iconBg}`}>
            {isSaved ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <StageIcon className={`h-5 w-5 ${colors.iconColor}`} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-sm">{stageName}</p>
              {isSaved ? (
                <Badge className="text-[10px] rounded-full bg-green-500/10 text-green-600 border-green-500/20 font-medium">
                  Saved
                </Badge>
              ) : selectedCount > 0 ? (
                <Badge className="text-[10px] rounded-full bg-primary/10 text-primary border-primary/20 font-medium">
                  {selectedCount} format{selectedCount !== 1 ? "s" : ""}
                </Badge>
              ) : null}
            </div>
            {stage?.objective && (
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{stage.objective}</p>
            )}
          </div>
        </div>

        {/* Stage strategy accordion */}
        {(stage?.psychological_task || stage?.messaging_focus || stage?.emotional_job) && (
          <div className="mt-3">
            <button
              onClick={() => setShowStrategy((v) => !v)}
              className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
              data-testid={`btn-toggle-strategy-${stageIndex}`}
            >
              {showStrategy ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {showStrategy ? "Hide strategy" : "View stage strategy"}
            </button>
            {showStrategy && (
              <div className="mt-2.5 grid grid-cols-1 gap-2">
                {stage.psychological_task && (
                  <div className="rounded-lg bg-background/60 border px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-0.5">Psychological task</p>
                    <p className="text-xs leading-relaxed">{stage.psychological_task}</p>
                  </div>
                )}
                {stage.messaging_focus && (
                  <div className="rounded-lg bg-background/60 border px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-0.5">Messaging focus</p>
                    <p className="text-xs leading-relaxed">{stage.messaging_focus}</p>
                  </div>
                )}
                {stage.emotional_job && (
                  <div className="rounded-lg bg-background/60 border px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-0.5">Emotional job</p>
                    <p className="text-xs leading-relaxed">{stage.emotional_job}</p>
                  </div>
                )}
                {stage.message_style && (
                  <div className="rounded-lg bg-background/60 border px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-0.5">Message style</p>
                    <p className="text-xs leading-relaxed">{stage.message_style}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Input summary bar ── */}
      <div className="flex items-center gap-3 px-5 py-2.5 border-b bg-muted/20">
        {stageStaticTarget !== null && stageStaticTarget > 0 && (
          <span className="text-xs font-semibold text-primary/80" data-testid={`text-static-target-${stageIndex}`}>
            {stageStaticTarget} statics planned
          </span>
        )}
        {stageStaticTarget !== null && stageStaticTarget > 0 && selectedCount > 0 && (
          <span className="text-xs text-muted-foreground/40">·</span>
        )}
        {selectedCount > 0 && (
          <span className="text-xs text-muted-foreground">
            {selectedCount} format{selectedCount !== 1 ? "s" : ""} in mix
          </span>
        )}
      </div>

      {/* ── Body: prescribed mix (after generation) or pre-selected formats (before) ── */}
      {aiResult ? (
        <div className="p-4 space-y-3">
          {/* Strategy rationale */}
          {aiResult.executive_summary && (
            <p className="text-xs text-muted-foreground leading-relaxed">{aiResult.executive_summary}</p>
          )}

          {/* Prescribed mix — format headers with individual ad slots */}
          {aiResult.selected_ad_units.length > 0 && (() => {
            // Deduplicate by format name, summing quantities
            const dedupedMap = new Map<string, StageAiSelectedUnit>();
            for (const u of aiResult.selected_ad_units) {
              const key = u.ad_unit_name;
              const ex = dedupedMap.get(key);
              if (ex) {
                dedupedMap.set(key, { ...ex, quantity: (ex.quantity ?? 1) + (u.quantity ?? 1) });
              } else {
                dedupedMap.set(key, { ...u });
              }
            }
            const deduped = Array.from(dedupedMap.values());

            // Build global slot list: [{formatName, slotGlobal, slotLocal}]
            let globalIdx = 0;
            const groups = deduped.map((unit) => {
              const qty = unit.quantity ?? 1;
              const slots = Array.from({ length: qty }, (_, li) => ({ global: ++globalIdx, local: li + 1 }));
              return { unit, slots };
            });

            // Pre-group angle results by ad_unit name for easy lookup
            const anglesByUnit = new Map<string, AngleAssignedUnit[]>();
            if (angleResult?.ad_unit_selections) {
              for (const a of angleResult.ad_unit_selections) {
                const key = a.ad_unit;
                if (!anglesByUnit.has(key)) anglesByUnit.set(key, []);
                anglesByUnit.get(key)!.push(a);
              }
            }

            return (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">Prescribed mix</p>
                <div className="rounded-xl border overflow-hidden divide-y">
                  {groups.map(({ unit, slots }) => {
                    const unitAngles = anglesByUnit.get(unit.ad_unit_name) ?? [];
                    return (
                      <div key={unit.ad_unit_name}>
                        {/* Format group header */}
                        <div className="flex items-center gap-2.5 px-3 py-2 bg-muted/40">
                          <div className="w-5 h-5 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Layers className="h-3 w-3 text-primary" />
                          </div>
                          <span className="text-xs font-semibold flex-1">{unit.ad_unit_name}</span>
                          <Badge className="text-[10px] rounded-full bg-primary/10 text-primary border-primary/20 font-semibold px-2 h-5">
                            ×{unit.quantity ?? 1}
                          </Badge>
                        </div>
                        {/* Individual ad slots */}
                        <div className="divide-y">
                          {slots.map(({ global, local }) => {
                            const assigned = unitAngles[local - 1] ?? null;
                            const imgJob = assigned
                              ? stageImageJobs?.find((j) => j.adConceptName === assigned.ad_concept_name) ?? null
                              : stageImageJobs?.find((j) => j.adUnitName.toUpperCase().trim() === unit.ad_unit_name.toUpperCase().trim()) ?? null;
                            return (
                              <div
                                key={local}
                                className="px-3 py-2.5 bg-background"
                                data-testid={`ad-slot-${stageIndex}-${unit.ad_unit_name}-${local}`}
                              >
                                <div className="flex items-start gap-3">
                                  <span className="text-[10px] font-mono text-muted-foreground/50 w-4 flex-shrink-0 text-right mt-0.5">{global}</span>
                                  {assigned ? (
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-semibold text-foreground truncate">{assigned.ad_concept_name}</p>
                                      <p className="text-[11px] text-primary truncate">{assigned.selected_angle}</p>
                                      {assigned.hook_direction && (
                                        <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">{assigned.hook_direction}</p>
                                      )}
                                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                        <span className="text-[9px] uppercase tracking-wider font-semibold text-muted-foreground/70 bg-muted px-1.5 py-0.5 rounded">
                                          {assigned.diversification_role}
                                        </span>
                                        <span className="text-[9px] uppercase tracking-wider font-semibold text-muted-foreground/70 bg-muted px-1.5 py-0.5 rounded">
                                          {assigned.tone}
                                        </span>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <span className="text-xs font-medium text-foreground/70">{unit.ad_unit_name} Ad {local}</span>
                                      <span className="ml-auto text-[10px] text-muted-foreground/40 italic">
                                        {isAssigningAngles ? "assigning…" : "angle unassigned"}
                                      </span>
                                    </>
                                  )}
                                </div>
                                {imgJob && (
                                  <div className="mt-2 ml-7">
                                    {imgJob.status === "generating" && (
                                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-violet-500/5 border border-violet-500/10">
                                        <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-500" />
                                        <span className="text-[10px] text-violet-500 font-medium">Generating image...</span>
                                      </div>
                                    )}
                                    {imgJob.status === "completed" && imgJob.imageUrl && (
                                      <div className="space-y-1.5">
                                        <button
                                          type="button"
                                          onClick={() => setLightboxUrl(imgJob.imageUrl)}
                                          className="rounded-lg overflow-hidden border border-border bg-muted/30 cursor-pointer hover:border-violet-400 hover:shadow-md transition-all group relative"
                                          data-testid={`asset-image-${stageIndex}-${global}`}
                                        >
                                          <img
                                            src={imgJob.imageUrl}
                                            alt={imgJob.adConceptName}
                                            className="w-full max-w-[280px] h-auto rounded-lg"
                                          />
                                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                                            <Eye className="w-5 h-5 text-white drop-shadow-md" />
                                          </div>
                                        </button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 px-2 text-[10px]"
                                          onClick={() => setInpaintJobId(imgJob.id)}
                                          data-testid={`btn-edit-stage-image-${stageIndex}-${global}`}
                                        >
                                          <Paintbrush className="w-3 h-3 mr-1" />
                                          Edit Image
                                        </Button>
                                        {inpaintJobId === imgJob.id && imgJob.imageUrl && (
                                          <InpaintingModal
                                            open={true}
                                            onClose={() => setInpaintJobId(null)}
                                            imageUrl={imgJob.imageUrl}
                                            onAccept={(newUrl) => {
                                              onUpdateStageImageUrl?.(imgJob.id, newUrl);
                                              setInpaintJobId(null);
                                            }}
                                            referenceImageUrls={referenceImageUrls}
                                            brandId={brandId}
                                          />
                                        )}
                                      </div>
                                    )}
                                    {imgJob.status === "failed" && (
                                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/5 border border-red-500/10">
                                        <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                                        <span className="text-[10px] text-red-500 font-medium truncate">{imgJob.error ?? "Generation failed"}</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Footer row */}
          <div className="flex items-center justify-between pt-1">
            <span className="text-[11px] text-muted-foreground">
              {aiResult.selected_ad_units.reduce((sum, u) => sum + (u.quantity ?? 1), 0)} total ads
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={onGenerate}
                disabled={isGeneratingPlan}
                className="rounded-full gap-1.5 h-7 text-xs text-muted-foreground px-2"
                data-testid={`btn-regenerate-mix-${stageIndex}`}
              >
                {isGeneratingPlan
                  ? <><Loader2 className="h-3 w-3 animate-spin" />Generating…</>
                  : <><RefreshCw className="h-3 w-3" />Regenerate mix</>
                }
              </Button>
              <Button
                size="sm"
                onClick={onAssignAngles}
                disabled={isGeneratingPlan || isAssigningAngles}
                className="rounded-full gap-1.5 h-7 text-xs bg-primary text-primary-foreground hover:bg-primary/90 px-3"
                data-testid={`btn-assign-angles-${stageIndex}`}
              >
                {isAssigningAngles
                  ? <><Loader2 className="h-3 w-3 animate-spin" />Assigning…</>
                  : <><Wand2 className="h-3 w-3" />{angleResult ? "Reassign angles" : "Assign angles"}</>
                }
              </Button>
              {angleResult && !isSaved && (
                <Button
                  size="sm"
                  onClick={onSaveStage}
                  className="rounded-full gap-1.5 h-7 text-xs bg-green-600 text-white hover:bg-green-700 px-3"
                  data-testid={`btn-save-stage-${stageIndex}`}
                >
                  <CheckCircle2 className="h-3 w-3" />Save stage
                </Button>
              )}
            </div>
          </div>

          {angleResult && (
            <div className="border-t border-border px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <Image className="w-3.5 h-3.5 text-violet-500" />
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Assets</span>
                  </div>
                  {stageImageCounts && (stageImageCounts.generating > 0 || stageImageCounts.completed > 0 || stageImageCounts.failed > 0) && (
                    <div className="flex items-center gap-2 text-[10px]">
                      {stageImageCounts.completed > 0 && (
                        <span className="text-emerald-500 font-medium">{stageImageCounts.completed} done</span>
                      )}
                      {stageImageCounts.generating > 0 && (
                        <span className="text-violet-500 font-medium flex items-center gap-1">
                          <Loader2 className="w-2.5 h-2.5 animate-spin" />{stageImageCounts.generating} active
                        </span>
                      )}
                      {stageImageCounts.failed > 0 && (
                        <span className="text-red-500 font-medium">{stageImageCounts.failed} failed</span>
                      )}
                    </div>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={onGenerateStageAssets}
                  disabled={isGeneratingPlan || isAssigningAngles || (stageImageCounts?.idle === 0 && (stageImageCounts?.generating ?? 0) > 0)}
                  className="rounded-full gap-1.5 h-7 text-xs bg-violet-600 text-white hover:bg-violet-700 px-3"
                  data-testid={`btn-generate-assets-${stageIndex}`}
                >
                  {stageImageCounts && stageImageCounts.generating > 0 ? (
                    <><Loader2 className="h-3 w-3 animate-spin" />Generating...</>
                  ) : stageImageCounts && stageImageCounts.completed > 0 && stageImageCounts.idle === 0 ? (
                    <><RefreshCw className="h-3 w-3" />Regenerate assets</>
                  ) : (
                    <><Sparkles className="h-3 w-3" />Generate assets</>
                  )}
                </Button>
              </div>
              {stageImageCounts && stageImageCounts.total > 0 && (stageImageCounts.completed > 0 || stageImageCounts.generating > 0) && (
                <Progress
                  value={stageImageCounts.total > 0 ? Math.round((stageImageCounts.completed / stageImageCounts.total) * 100) : 0}
                  className="h-1 mt-2"
                />
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="p-4 space-y-3">
          {selectedCount === 0 ? (
            <div className="flex items-center gap-3 py-2.5 px-4 rounded-xl border border-dashed text-muted-foreground">
              <Layers className="h-4 w-4 flex-shrink-0" />
              <p className="text-xs">No formats pre-selected — AI will choose from all available formats for this stage.</p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {displayFormatNames.map((name) => (
                <div key={name} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border bg-muted/30 text-xs font-medium">
                  <Layers className="h-3 w-3 text-muted-foreground" />
                  {name}
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-end">
            <Button
              size="sm"
              onClick={onGenerate}
              disabled={isGeneratingPlan}
              className="rounded-full gap-1.5 h-8 border-primary/30 text-primary hover:bg-primary/5 bg-transparent border"
              data-testid={`btn-generate-ad-plan-${stageIndex}`}
            >
              {isGeneratingPlan
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Generating…</>
                : <><Sparkles className="h-3.5 w-3.5" />Generate diversity mix</>
              }
            </Button>
          </div>
        </div>
      )}
    </Card>

    {lightboxUrl && (
      <div
        className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-6 cursor-pointer"
        onClick={() => setLightboxUrl(null)}
        data-testid={`lightbox-overlay-${stageIndex}`}
      >
        <div className="relative max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
          <img
            src={lightboxUrl}
            alt="Generated asset"
            className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
          />
          <button
            type="button"
            onClick={() => setLightboxUrl(null)}
            className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-white/90 text-black flex items-center justify-center shadow-lg hover:bg-white transition-colors"
            data-testid={`lightbox-close-${stageIndex}`}
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      </div>
    )}
  </>);
}

// ─── Main step ────────────────────────────────────────────────────────────────

export function CreativeBriefsStep({
  creativeBriefs,
  product,
  selectedVariantName,
  persona,
  brand,
  funnelStrategy,
  campaignPlan,
  plannerPersona,
  creativeStrategist,
  adUnitSelections,
  adUnitPlan,
  narrativeAngles,
  narrativeOpportunity,
  mediaAllocation,
  mediaMix,
  onUpdate,
  onUpdateCreativeStrategist,
  onUpdateAdUnitSelections,
  onUpdateMediaAllocation,
  isStale,
  onClearStale,
  draftId,
  draftName,
  onRegisterSave,
  onResetDraft,
}: CreativeBriefsStepProps) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [isSaving, setIsSaving] = useState(false);

  const [localQueue, setLocalQueue] = useState<BriefJob[]>(() =>
    Array.isArray(creativeBriefs.queue) ? creativeBriefs.queue : []
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [liveStatusMsg, setLiveStatusMsg] = useState(LIVE_STATUS_MESSAGES[0]);
  const [activeStage, setActiveStage] = useState<string | null>(null);
  const [isGeneratingStrategist, setIsGeneratingStrategist] = useState(false);
  const [strategistError, setStrategistError] = useState<string | null>(null);
  const [generatingStages, setGeneratingStages] = useState<Record<number, boolean>>({});
  // Hydrate from the persisted creativeBriefs slice so the diversity mix,
  // angle assignments, and saved-stage flags survive navigation away from
  // and back to this step. They get re-saved by the effects further down.
  const [stageAiResults, setStageAiResults] = useState<Record<string, StageAiResult>>(
    (creativeBriefs?.stageAiResults as Record<string, StageAiResult> | undefined) ?? {}
  );
  const [assigningAnglesStages, setAssigningAnglesStages] = useState<Record<number, boolean>>({});
  const [stageAngleResults, setStageAngleResults] = useState<Record<string, AngleAssignmentResult>>(
    (creativeBriefs?.stageAngleResults as Record<string, AngleAssignmentResult> | undefined) ?? {}
  );
  const [savedStages, setSavedStages] = useState<Record<string, boolean>>(
    creativeBriefs?.savedStages ?? {}
  );

  const prevStaleRef = useRef(isStale);
  const [isRunningFullPipeline, setIsRunningFullPipeline] = useState(false);
  const [fullPipelineStatus, setFullPipelineStatus] = useState<string | null>(null);
  const { data: brandAdUnits } = useQuery<any[]>({
    queryKey: ["/api/brands", brand?.id, "ad-units"],
    queryFn: async () => {
      if (!brand?.id) return [];
      const res = await fetch(`/api/brands/${brand.id}/ad-units`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!brand?.id,
  });

  const [stageImageJobs, setStageImageJobs] = useState<Record<string, Array<{
    id: string;
    adUnitName: string;
    adConceptName: string;
    status: "idle" | "generating" | "completed" | "failed";
    imageUrl: string | null;
    error: string | null;
  }>>>(creativeBriefs?.stageImageJobs ?? {});

  const queueRef = useRef<BriefJob[]>(localQueue);
  const isProcessingRef = useRef(false);
  const isPausedRef = useRef(false);
  const onUpdateRef = useRef(onUpdate);
  const onUpdateMediaAllocationRef = useRef(onUpdateMediaAllocation);
  const creativeBriefsRef = useRef(creativeBriefs);
  const statusMsgTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => { queueRef.current = localQueue; }, [localQueue]);
  useEffect(() => { onUpdateRef.current = onUpdate; }, [onUpdate]);
  useEffect(() => { onUpdateMediaAllocationRef.current = onUpdateMediaAllocation; }, [onUpdateMediaAllocation]);
  useEffect(() => { creativeBriefsRef.current = creativeBriefs; }, [creativeBriefs]);

  // Persist per-stage state (image jobs, diversity mix, angle assignments,
  // saved-stage flags) so it survives navigation away from this step. All
  // four fields are persisted in a single onUpdate call so concurrent
  // updates can't clobber each other via stale spreads of
  // creativeBriefsRef.current. We only write when at least one field is
  // non-empty so we don't overwrite freshly hydrated state with an empty
  // on first render. Explicit clears (stale-effect, manual reset) push
  // the empty values directly via onUpdateRef.
  useEffect(() => {
    const hasAny =
      Object.keys(stageImageJobs).length > 0 ||
      Object.keys(stageAiResults).length > 0 ||
      Object.keys(stageAngleResults).length > 0 ||
      Object.keys(savedStages).length > 0;
    if (hasAny) {
      onUpdateRef.current({
        ...creativeBriefsRef.current,
        stageImageJobs,
        stageAiResults,
        stageAngleResults,
        savedStages,
      });
    }
  }, [stageImageJobs, stageAiResults, stageAngleResults, savedStages]);

  useEffect(() => {
    if (
      isStale &&
      !prevStaleRef.current &&
      (localQueue.length > 0 ||
        Object.keys(stageImageJobs).length > 0 ||
        Object.keys(stageAiResults).length > 0 ||
        Object.keys(stageAngleResults).length > 0 ||
        Object.keys(savedStages).length > 0)
    ) {
      setLocalQueue([]);
      queueRef.current = [];
      setStageAiResults({});
      setStageAngleResults({});
      setSavedStages({});
      setStageImageJobs({});
      onUpdateRef.current({
        ...creativeBriefsRef.current,
        queue: [],
        completedCount: 0,
        status: "empty",
        stageImageJobs: {},
        stageAiResults: {},
        stageAngleResults: {},
        savedStages: {},
      });
    }
    prevStaleRef.current = isStale;
  }, [isStale]);

  useEffect(() => {
    if (!isProcessingRef.current) {
      const migratedQueue = creativeBriefs.queue.map((j) => ({
        ...j,
        generatedImageUrl: j.generatedImageUrl ?? null,
        imageStatus: j.imageStatus ?? ("idle" as const),
        imageError: j.imageError ?? null,
      }));
      setLocalQueue(migratedQueue);
      queueRef.current = migratedQueue;
    }
  }, [creativeBriefs.queue]);

  const persistQueue = useCallback((queue: BriefJob[], status: CreativeBriefsState["status"]) => {
    const counts = computeCounts(queue);
    // Spread the latest creativeBriefs first so we preserve the per-stage
    // fields (stageImageJobs, stageAiResults, stageAngleResults,
    // savedStages). setCreativeBriefs is a full replace at the parent, so
    // any field we omit here would be dropped.
    onUpdateRef.current({
      ...creativeBriefsRef.current,
      status,
      generatedAt: status === "generated"
        ? new Date().toISOString()
        : creativeBriefsRef.current.generatedAt,
      queue,
      completedCount: counts.completed,
      failedCount: counts.failed,
      totalCount: queue.length,
      currentJobId: queue.find((j) => j.status === "running")?.jobId ?? null,
      error: null,
    });
  }, []);

  const updateJob = useCallback((jobId: string, updates: Partial<BriefJob>) => {
    setLocalQueue((prev) => {
      const next = prev.map((j) => (j.jobId === jobId ? { ...j, ...updates } : j));
      queueRef.current = next;
      return next;
    });
  }, []);

  const startStatusMessages = useCallback(() => {
    let idx = 0;
    if (statusMsgTimerRef.current) clearInterval(statusMsgTimerRef.current);
    statusMsgTimerRef.current = setInterval(() => {
      idx = (idx + 1) % LIVE_STATUS_MESSAGES.length;
      setLiveStatusMsg(LIVE_STATUS_MESSAGES[idx]);
    }, 2500);
  }, []);

  const stopStatusMessages = useCallback(() => {
    if (statusMsgTimerRef.current) {
      clearInterval(statusMsgTimerRef.current);
      statusMsgTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => stopStatusMessages(), [stopStatusMessages]);

  const generateImageForJob = useCallback(
    async (job: BriefJob) => {
      if (job.format !== "static") return;
      updateJob(job.jobId, { imageStatus: "generating" });

      const angle = job.angleId
        ? narrativeAngles.find((a) => a.id === job.angleId) ?? null
        : null;

      try {
        const res = await fetchCampaignImage("/api/generate-campaign-ad-image", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            adUnitId: job.adUnitId,
            product: product ?? null,
            // Task #744 — selected variant (null when product absent or "no specific variant").
            selectedVariantName: product ? selectedVariantName : null,
            persona: persona ?? null,
            narrativeAngle: angle,
            narrativeOpportunity: narrativeOpportunity ?? null,
            brand: brand ?? null,
            referenceImageUrl: job.referenceImageUrl ?? null,
          }),
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error((errData as Record<string, string>).message ?? `Image generation failed`);
        }

        const data = await res.json();
        console.log(
          "[CampaignAdImage] Generated for",
          job.adUnitName,
          "ref:",
          job.referenceImageUrl?.slice(0, 80) ?? "(none)",
          "→",
          data.image_url?.slice(0, 80)
        );
        updateJob(job.jobId, {
          generatedImageUrl: data.image_url,
          imageStatus: "completed",
          imageError: null,
        });
        persistQueue(queueRef.current, creativeBriefsRef.current.status);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        console.error("[CampaignAdImage] Failed for", job.adUnitName, msg);
        updateJob(job.jobId, { imageStatus: "failed", imageError: msg });
        persistQueue(queueRef.current, creativeBriefsRef.current.status);
      }
    },
    [product, selectedVariantName, persona, brand, narrativeAngles, narrativeOpportunity, updateJob, persistQueue]
  );

  const processSingleJob = useCallback(
    async (job: BriefJob): Promise<BriefJob> => {
      updateJob(job.jobId, { status: "running" });

      const draftForPayload = {
        product,
        persona,
        funnelStrategy,
        narrativeAngles,
        campaignPlan,
        adUnitSelections,
        creativeBriefs: creativeBriefsRef.current,
        id: "", name: "", narrativeOpportunity: null, mediaMix: null,
        adUnitPlan: null, staleness: {} as any,
        currentStep: "creativeBriefs" as const, completedSteps: [] as any[],
      };

      const payload = buildCreativeBriefPayload(job, draftForPayload as any, brand);

      try {
        const res = await fetch("/api/generate-creative-brief", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error((errData as Record<string, string>).message ?? `Server returned ${res.status}`);
        }

        const raw = await res.json();
        console.log("[CreativeBrief] Raw for", job.adUnitName, ":", JSON.stringify(raw).slice(0, 300));

        const brief = normalizeCreativeBriefResponse(raw);
        const validationErrors = validateCreativeBrief(brief);
        if (validationErrors.length > 0) {
          throw new Error(`Incomplete brief: ${validationErrors.join("; ")}`);
        }

        const completedJob: BriefJob = { ...job, status: "completed", brief, error: null };
        updateJob(job.jobId, { status: "completed", brief, error: null });

        if (job.format === "static") {
          generateImageForJob(completedJob);
        }

        return completedJob;
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        console.error("[CreativeBrief] Failed:", job.adUnitName, msg);
        const failedJob: BriefJob = { ...job, status: "failed", error: msg };
        updateJob(job.jobId, { status: "failed", error: msg });
        return failedJob;
      }
    },
    [product, persona, brand, funnelStrategy, narrativeAngles, campaignPlan, adUnitSelections, updateJob, generateImageForJob]
  );

  const runQueue = useCallback(async () => {
    if (isProcessingRef.current) return;
    if (isPausedRef.current) return;
    isProcessingRef.current = true;
    setIsProcessing(true);
    startStatusMessages();

    while (!isPausedRef.current) {
      const nextJob = queueRef.current.find((j) => j.status === "pending");
      if (!nextJob) break;

      await processSingleJob(nextJob);

      const latest = queueRef.current;
      const allSettled = latest.every(
        (j) => j.status === "completed" || j.status === "failed" || j.status === "locked"
      );
      persistQueue(latest, allSettled ? "generated" : "generating");
    }

    stopStatusMessages();
    isProcessingRef.current = false;

    const finalQueue = queueRef.current;
    const allDone = finalQueue.every(
      (j) => j.status === "completed" || j.status === "failed" || j.status === "locked"
    );
    const finalStatus: CreativeBriefsState["status"] = isPausedRef.current
      ? "paused"
      : allDone
        ? "generated"
        : "paused";

    persistQueue(finalQueue, finalStatus);
    setIsProcessing(false);

    if (!isPausedRef.current) {
      const counts = computeCounts(finalQueue);
      toast({
        title: "Brief generation complete",
        description: `${counts.completed} of ${counts.total} briefs generated${counts.failed > 0 ? `, ${counts.failed} failed` : ""}.`,
      });
    }
  }, [processSingleJob, startStatusMessages, stopStatusMessages, persistQueue, toast]);

  const handleGenerate = useCallback(() => {
    const draft = {
      product, persona, funnelStrategy, narrativeAngles,
      adUnitSelections, campaignPlan,
      id: "", name: "", brand: null,
      narrativeOpportunity: null, mediaMix: null, adUnitPlan: null,
      creativeBriefs: creativeBriefsRef.current,
      staleness: {} as any, currentStep: "creativeBriefs" as const, completedSteps: [] as any[],
    };
    const jobs = buildCreativeBriefJobs(draft as any);
    console.log("[CreativeBrief] Built", jobs.length, "jobs from", adUnitSelections.length, "selections");
    setLocalQueue(jobs);
    queueRef.current = jobs;
    persistQueue(jobs, "generating");
    onClearStale();
    isPausedRef.current = false;
    setIsPaused(false);
    setTimeout(() => runQueue(), 0);
  }, [product, persona, funnelStrategy, narrativeAngles, adUnitSelections, campaignPlan, persistQueue, onClearStale, runQueue]);

  const handleResume = useCallback(() => {
    isPausedRef.current = false;
    setIsPaused(false);
    runQueue();
  }, [runQueue]);

  const handlePause = useCallback(() => {
    isPausedRef.current = true;
    setIsPaused(true);
  }, []);

  const handleRetryFailed = useCallback(() => {
    setLocalQueue((prev) => {
      const next = prev.map((j) =>
        j.status === "failed" ? { ...j, status: "pending" as const, error: null, generatedImageUrl: null, imageStatus: "idle" as const, imageError: null } : j
      );
      queueRef.current = next;
      persistQueue(next, isPausedRef.current ? "paused" : "generating");
      return next;
    });
    if (!isPausedRef.current && !isProcessingRef.current) setTimeout(() => runQueue(), 0);
  }, [runQueue, persistQueue]);

  const handleRetry = useCallback(
    (jobId: string) => {
      setLocalQueue((prev) => {
        const next = prev.map((j) =>
          j.jobId === jobId ? { ...j, status: "pending" as const, error: null, generatedImageUrl: null, imageStatus: "idle" as const, imageError: null } : j
        );
        queueRef.current = next;
        return next;
      });
      if (!isPausedRef.current && !isProcessingRef.current) setTimeout(() => runQueue(), 0);
    },
    [runQueue]
  );

  const handleRegenerate = useCallback(
    (jobId: string) => {
      setLocalQueue((prev) => {
        const job = prev.find((j) => j.jobId === jobId);
        if (!job || job.status === "locked") return prev;
        const next = prev.map((j) =>
          j.jobId === jobId ? { ...j, status: "pending" as const, brief: null, error: null, generatedImageUrl: null, imageStatus: "idle" as const, imageError: null } : j
        );
        queueRef.current = next;
        return next;
      });
      if (!isPausedRef.current && !isProcessingRef.current) setTimeout(() => runQueue(), 0);
    },
    [runQueue]
  );

  const handleToggleLock = useCallback((jobId: string) => {
    setLocalQueue((prev) => {
      const next = prev.map((j) =>
        j.jobId === jobId
          ? { ...j, status: (j.status === "locked" ? "completed" : "locked") as BriefJob["status"] }
          : j
      );
      queueRef.current = next;
      persistQueue(next, creativeBriefsRef.current.status);
      return next;
    });
  }, [persistQueue]);

  const handleGenerateImage = useCallback(
    (jobId: string) => {
      const job = queueRef.current.find((j) => j.jobId === jobId);
      if (!job || (job.status !== "completed" && job.status !== "locked")) return;
      generateImageForJob(job);
    },
    [generateImageForJob]
  );

  const handleUpdateImageUrl = useCallback(
    (jobId: string, newUrl: string) => {
      updateJob(jobId, { generatedImageUrl: newUrl });
      persistQueue(queueRef.current, creativeBriefsRef.current.status);
    },
    [updateJob, persistQueue]
  );

  const handleGenerateAllImages = useCallback(() => {
    const eligibleJobs = queueRef.current.filter(
      (j) =>
        j.format === "static" &&
        (j.status === "completed" || j.status === "locked") &&
        j.imageStatus !== "generating" &&
        j.imageStatus !== "completed"
    );
    if (eligibleJobs.length === 0) {
      toast({ title: "No images to generate", description: "All eligible briefs already have images or are in progress." });
      return;
    }
    for (const job of eligibleJobs) {
      if (job.imageStatus === "failed") {
        updateJob(job.jobId, { imageStatus: "idle", imageError: null });
      }
    }
    toast({ title: "Generating assets", description: `Starting image generation for ${eligibleJobs.length} brief${eligibleJobs.length > 1 ? "s" : ""}...` });
    for (const job of eligibleJobs) {
      generateImageForJob(job);
    }
  }, [generateImageForJob, updateJob, toast]);

  const handleRegenerateAll = useCallback(() => {
    setLocalQueue((prev) => {
      const next = prev.map((j) =>
        j.status === "locked" ? j : { ...j, status: "pending" as const, brief: null, error: null, generatedImageUrl: null, imageStatus: "idle" as const, imageError: null }
      );
      queueRef.current = next;
      persistQueue(next, isPausedRef.current ? "paused" : "generating");
      return next;
    });
    if (!isPausedRef.current && !isProcessingRef.current) setTimeout(() => runQueue(), 0);
  }, [runQueue, persistQueue]);

  const handleGenerateStrategist = useCallback(async () => {
    setIsGeneratingStrategist(true);
    setStrategistError(null);
    try {
      const res = await fetch("/api/generate-creative-strategist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          product: product ?? null,
          persona: persona ?? null,
          narrative_opportunity: narrativeOpportunity ?? null,
          narrative_angles: narrativeAngles ?? [],
          campaign_plan: campaignPlan ?? null,
          planner_persona: plannerPersona ?? null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as Record<string, string>).message ?? `Server returned ${res.status}`);
      }
      const data = await res.json();
      const extracted: CreativeStrategistPersona | null =
        data?.creative_strategist_persona ?? data?.output?.creative_strategist_persona ?? null;
      if (!extracted) throw new Error("No creative strategist data returned");
      onUpdateCreativeStrategist(extracted);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setStrategistError(msg);
      toast({ title: "Generation failed", description: msg, variant: "destructive" });
    } finally {
      setIsGeneratingStrategist(false);
    }
  }, [product, persona, narrativeOpportunity, narrativeAngles, campaignPlan, plannerPersona, onUpdateCreativeStrategist, toast]);

  const clearDiversityMix = useCallback(() => {
    // Wipe everything generated on the Creative Briefs page below the
    // strategist: per-stage AI diversity mix, angle assignments, saved
    // stage flags, generated stage images, and the brief queue itself.
    // Also drop the per-stage media allocation: it's what drives the
    // "X statics planned" badge on each stage card, and if the user is
    // resetting the diversity mix they want that count to reflect their
    // current selections (or be regenerated), not the old allocation.
    // Other upstream wizard data (ad unit selections, campaign plan,
    // planner, narrative angles) is left untouched.
    setLocalQueue([]);
    queueRef.current = [];
    setStageAiResults({});
    setStageAngleResults({});
    setSavedStages({});
    setStageImageJobs({});
    onUpdateMediaAllocationRef.current(null);
    onUpdateRef.current({
      ...creativeBriefsRef.current,
      queue: [],
      completedCount: 0,
      status: "empty",
      stageImageJobs: {},
      stageAiResults: {},
      stageAngleResults: {},
      savedStages: {},
    });
  }, []);

  // Must be declared before handleGeneratePlan to avoid temporal dead zone in dep array
  const inlineFunnelStages = funnelStrategy?.stages ?? [];
  const inlineTotalStages = inlineFunnelStages.length;

  const stageAvailableFormats = useMemo(() => {
    if (!brandAdUnits?.length || !inlineFunnelStages.length) return {} as Record<string, string[]>;
    const result: Record<string, string[]> = {};
    for (const fs of inlineFunnelStages) {
      const catalogGroup = stageToCatalogGroup(fs.stage, fs.stage_group ?? "");
      const matching = brandAdUnits.filter((u: any) => {
        const primary = (u.funnelStage || "").trim();
        const primaryLower = primary.toLowerCase();
        const secondary = (u.secondaryFunnel || "").trim();
        const unitCatalogGroup = stageToCatalogGroup(primary, "");
        return unitCatalogGroup === catalogGroup || primaryLower === fs.stage.toLowerCase() || secondary === fs.stage;
      });
      result[fs.stage] = Array.from(new Set(matching.map((u: any) => u.name))).filter(Boolean);
    }
    return result;
  }, [brandAdUnits, inlineFunnelStages]);

  // Per-stage AI ad unit selection: calls the stage-specific Sim workflow
  const handleGeneratePlan = useCallback(async (stageIdx: number) => {
    const stage = inlineFunnelStages[stageIdx];
    if (!brand?.id || !stage) return;
    setGeneratingStages((prev) => ({ ...prev, [stageIdx]: true }));
    try {
      // Fetch all ad units for the brand
      const auRes = await fetch(`/api/brands/${brand.id}/ad-units`, { credentials: "include" });
      if (!auRes.ok) throw new Error("Failed to load ad formats");
      const rawAdUnits: any[] = await auRes.json();
      if (!rawAdUnits.length) throw new Error("No ad formats found for this brand. Add formats in brand settings first.");

      // Determine which ad units to offer Sim:
      // Priority 1 — formats the user pre-selected in Ad Unit Selection step
      // Priority 2 — formats in the Ad Unit Plan for this stage (AI-planned)
      // Priority 3 — fall back to all units in the catalog group for this funnel stage
      const catalogGroup = stageToCatalogGroup(stage.stage, stage.stage_group ?? "");

      const stageSelectedIds = new Set(
        adUnitSelections
          .filter((s) => s.funnelStageId === catalogGroup || s.funnelStageId === stage.stage)
          .map((s) => s.adUnitId)
      );

      // Collect format names from the ad unit plan for this stage
      const planStagePlan = adUnitPlan?.stagePlans?.find(
        (sp) => sp.stage === stage.stage || sp.stage === catalogGroup
      );
      const planFormatNamesSet = new Set(
        planStagePlan?.ads?.map((a) => (a.ad_unit_name ?? "").toUpperCase().trim()) ?? []
      );

      let stageRawUnits: any[];
      let ads_to_select: number;

      // How many static ads to actually MAKE for this stage. The user's
      // ad-unit selections describe the POOL of formats those ads can come
      // from (e.g. "any of these 14 formats"), NOT how many to produce. The
      // production count comes from mediaMix.staticUnits divided across
      // funnel stages.
      //
      // Priority:
      //   1) AI per-stage allocation from the Campaign Plan (already
      //      validated against the budget by getPlanStaticForStage).
      //   2) Per-stage portion of the user's static-units budget.
      //   3) Default of 3 if the user hasn't specified a budget.
      const planStaticForStage = getPlanStaticForStage(
        mediaAllocation,
        stage.stage,
        stage.stage_group,
        stageIdx,
        mediaMix?.staticUnits ?? null,
      );
      const totalStages = Math.max(1, inlineFunnelStages.length);
      const wizardStaticUnits = mediaMix?.staticUnits ?? 0;
      const perStageStaticBudget = getStageBudgetPortion(wizardStaticUnits, stageIdx, totalStages);
      const allocationTarget = planStaticForStage > 0
        ? planStaticForStage
        : (perStageStaticBudget > 0 ? perStageStaticBudget : 3);

      const currentStageName = stage.stage;
      const matchesStage = (u: any) => {
        const primary = (u.funnelStage || "").trim();
        const primaryLower = primary.toLowerCase();
        const secondary = (u.secondaryFunnel || "").trim();
        const unitCatalogGroup = stageToCatalogGroup(primary, "");
        return unitCatalogGroup === catalogGroup || primaryLower === currentStageName.toLowerCase() || secondary === stage.stage;
      };

      {
        const selectedUnits = stageSelectedIds.size > 0
          ? rawAdUnits.filter((u: any) => stageSelectedIds.has(u.id))
          : [];
        const stageMatchedUnits = rawAdUnits.filter(matchesStage);
        const planMatchedUnits = planFormatNamesSet.size > 0
          ? rawAdUnits.filter((u: any) => planFormatNamesSet.has((u.name ?? "").toUpperCase().trim()))
          : [];

        const seenIds = new Set<string>();
        stageRawUnits = [];
        for (const u of [...selectedUnits, ...stageMatchedUnits, ...planMatchedUnits]) {
          if (!seenIds.has(u.id)) {
            seenIds.add(u.id);
            stageRawUnits.push(u);
          }
        }

        // ads_to_select = how many ads to MAKE for this stage. Always
        // the allocation target (per-stage budget). The selected ad-units
        // are the POOL the AI picks from, not the count to produce.
        ads_to_select = allocationTarget;

        if (stageRawUnits.length < ads_to_select) {
          const adjacentGroups: Record<string, string[]> = {
            tof: ["mof"],
            mof: ["tof", "bof"],
            bof: ["mof"],
          };
          const expandTo = adjacentGroups[catalogGroup] ?? [];
          for (const adj of expandTo) {
            const extra = rawAdUnits.filter(
              (u: any) => stageToCatalogGroup((u.funnelStage || "").trim(), "") === adj && !seenIds.has(u.id)
            );
            for (const u of extra) {
              stageRawUnits.push(u);
              seenIds.add(u.id);
            }
            if (stageRawUnits.length >= ads_to_select) break;
          }
        }
      }

      if (!stageRawUnits.length) throw new Error("No ad formats found for this stage. Go back to the Ad Unit Selection step to choose formats.");

      const available_ad_units = stageRawUnits.map((u: any) => ({
        id: u.id,
        name: u.name,
        format: u.formatType ?? "static",
        funnel_stage: u.funnelStage ?? "tof",
        objective: u.objective ?? "",
        strategic_role: (u.strategicRole ?? "").slice(0, 300),
      }));

      // user_ad_unit: intentionally empty. The user's ad-unit selections
      // are treated as the POOL to draw from (already in available_ad_units),
      // not as forced/must-include picks. The Sim workflow uses a non-empty
      // user_ad_unit to override its own ads_to_select target, which is the
      // wrong behavior under our budget-driven semantics.
      const user_ad_unit: Array<Record<string, unknown>> = [];

      const payload = {
        product: product ? {
          name: product.name,
          description: (product as any).description ?? (product as any).positioning ?? product.name,
          category: (product as any).category ?? "",
        } : { name: "Product", description: "" },
        persona: persona ? {
          name: persona.name,
          summary: (plannerPersona as any)?.summary ?? (persona as any).bio ?? persona.name,
          age_range: (persona as any).ageRange ?? "",
          primary_pain: (plannerPersona as any)?.primary_pain ?? "",
        } : { name: "Persona", summary: "" },
        brand: {
          name: brand.name,
          positioning: (brand as any).positioning ?? (brand as any).brandVoice ?? brand.name,
        },
        narrative_opportunity: narrativeOpportunity ? {
          title: narrativeOpportunity.title,
          summary: (narrativeOpportunity as any).summary ?? narrativeOpportunity.title,
        } : { title: "Campaign", summary: "" },
        selected_narrative_angles: narrativeAngles.slice(0, 5).map((a) => ({
          id: a.id,
          title: a.name,
          hook: (a as any).hook ?? a.name,
        })),
        funnel_stage: {
          stage: stage.stage,
          stage_group: stage.stage_group ?? catalogGroup,
          objective: stage.objective ?? "",
          psychological_task: stage.psychological_task ?? "",
          messaging_focus: stage.messaging_focus ?? "",
          emotional_job: stage.emotional_job ?? "",
          message_style: stage.message_style ?? "",
        },
        available_ad_units,
        ads_to_select,
        user_ad_unit,
        creative_strategist: creativeStrategist ? {
          name: creativeStrategist.name,
          archetype: creativeStrategist.archetype,
          mission: creativeStrategist.mission,
          strategic_mindset: creativeStrategist.strategic_mindset,
          signature_strength: creativeStrategist.signature_strength,
          creative_philosophy: creativeStrategist.creative_philosophy,
          audience_and_funnel_instincts: creativeStrategist.audience_and_funnel_instincts,
          decision_framework: creativeStrategist.decision_framework,
        } : null,
      };

      const res = await fetch("/api/generate-stage-ad-selection", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).message ?? `Server returned ${res.status}`);
      }

      const result = await res.json();
      const stageName = stage.stage;

      // Store the AI result for display only — diversity mix is a planning view,
      // it does NOT modify the ad unit selections from the prior step.
      setStageAiResults((prev) => ({
        ...prev,
        [stageName]: {
          executive_summary: result?.executive_summary ?? "",
          selected_ad_units: result?.selected_ad_units ?? [],
        },
      }));
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Selection generation failed", description: msg, variant: "destructive" });
    } finally {
      setGeneratingStages((prev) => { const next = { ...prev }; delete next[stageIdx]; return next; });
    }
  }, [brand, inlineFunnelStages, product, persona, plannerPersona, narrativeOpportunity, narrativeAngles, mediaAllocation, mediaMix, adUnitSelections, adUnitPlan, creativeStrategist, toast]);

  const handleAssignAngles = useCallback(async (stageIdx: number) => {
    const stage = inlineFunnelStages[stageIdx];
    if (!stage) return;
    const stageName = stage.stage;
    const aiResult = stageAiResults[stageName];
    if (!aiResult) return;

    // Deduplicate ad units and build the list with quantities
    const dedupedMap = new Map<string, number>();
    for (const u of aiResult.selected_ad_units) {
      dedupedMap.set(u.ad_unit_name, (dedupedMap.get(u.ad_unit_name) ?? 0) + (u.quantity ?? 1));
    }
    const ad_units = Array.from(dedupedMap.entries()).map(([name, qty]) => ({
      ad_unit_name: name,
      quantity: qty,
    }));

    setAssigningAnglesStages((prev) => ({ ...prev, [stageIdx]: true }));
    try {
      const res = await fetch("/api/assign-angles", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          product,
          persona,
          narrative_opportunity: narrativeOpportunity,
          narrative_angles: narrativeAngles,
          funnel_stage: stageName,
          funnel_strategy: funnelStrategy,
          ad_units,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).message ?? `Server returned ${res.status}`);
      }

      const result = await res.json();
      setStageAngleResults((prev) => ({ ...prev, [stageName]: result }));
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Angle assignment failed", description: msg, variant: "destructive" });
    } finally {
      setAssigningAnglesStages((prev) => { const next = { ...prev }; delete next[stageIdx]; return next; });
    }
  }, [inlineFunnelStages, stageAiResults, product, persona, narrativeOpportunity, narrativeAngles, funnelStrategy, toast]);

  const handleSaveStage = useCallback((stageIdx: number) => {
    const stage = inlineFunnelStages[stageIdx];
    if (!stage) return;
    setSavedStages((prev) => ({ ...prev, [stage.stage]: true }));
    toast({ title: `${stage.stage} saved`, description: "You can now proceed to the next stage." });
  }, [inlineFunnelStages, toast]);

  const handleGenerateStageAssets = useCallback(async (stageIdx: number) => {
    const stage = inlineFunnelStages[stageIdx];
    if (!stage || !brand?.id) return;
    const stageName = stage.stage;
    const angleResult = stageAngleResults[stageName];
    const aiResult = stageAiResults[stageName];
    if (!angleResult?.ad_unit_selections?.length && !aiResult?.selected_ad_units?.length) {
      toast({ title: "No ads to generate", description: "Generate a diversity mix and assign angles first." });
      return;
    }

    try {
      // Compute the planned target for this stage. Mirrors handleGeneratePlan
      // exactly: plan -> per-stage budget portion -> default 3. The badge on
      // the stage card uses the same chain (sans the default), so the badge
      // and the actual jobs.length match.
      const planStaticForStage = getPlanStaticForStage(
        mediaAllocation,
        stage.stage,
        stage.stage_group,
        stageIdx,
        mediaMix?.staticUnits ?? null,
      );
      const totalStages = Math.max(1, inlineFunnelStages.length);
      const wizardStaticUnits = mediaMix?.staticUnits ?? 0;
      const perStageStaticBudget = getStageBudgetPortion(wizardStaticUnits, stageIdx, totalStages);
      const targetCount = planStaticForStage > 0
        ? planStaticForStage
        : (perStageStaticBudget > 0 ? perStageStaticBudget : 3);

      // Build asset-generation list from the AI mix expanded by quantity, so
      // we generate one image per slot in the Prescribed Mix (not one per
      // unique ad_unit_name). Pull the angle from angleResult by matching
      // ad_unit name and slot index, cycling/falling back if the angle
      // workflow returned fewer concepts than there are slots.
      const ads: Array<{ ad_concept_name: string; ad_unit: string; selected_angle: string }> = [];
      const anglesByUnit = new Map<string, AngleAssignedUnit[]>();
      if (angleResult?.ad_unit_selections?.length) {
        for (const a of angleResult.ad_unit_selections) {
          const key = a.ad_unit;
          if (!anglesByUnit.has(key)) anglesByUnit.set(key, []);
          anglesByUnit.get(key)!.push(a);
        }
      }
      if (aiResult?.selected_ad_units?.length) {
        // Deduplicate aiResult by name, summing quantities — same as the UI.
        const dedupedMap = new Map<string, number>();
        for (const u of aiResult.selected_ad_units) {
          dedupedMap.set(u.ad_unit_name, (dedupedMap.get(u.ad_unit_name) ?? 0) + ((u as any).quantity ?? 1));
        }
        for (const [name, qty] of dedupedMap.entries()) {
          const unitAngles = anglesByUnit.get(name) ?? [];
          for (let slot = 0; slot < qty; slot++) {
            const assigned = unitAngles[slot] ?? null;
            ads.push({
              ad_concept_name: assigned?.ad_concept_name ?? `${name} Ad ${slot + 1}`,
              ad_unit: name,
              selected_angle: assigned?.selected_angle ?? "",
            });
          }
        }
      } else if (angleResult?.ad_unit_selections?.length) {
        for (const sel of angleResult.ad_unit_selections) {
          ads.push({ ad_concept_name: sel.ad_concept_name, ad_unit: sel.ad_unit, selected_angle: sel.selected_angle });
        }
      }

      // Clamp to the planned target so we never make MORE ads than promised by
      // the badge. We don't pad up if the AI returned fewer — that would
      // fabricate slots without real angle/concept data.
      if (ads.length > targetCount) {
        ads.length = targetCount;
      }

      const auRes = await fetch(`/api/brands/${brand.id}/ad-units`, { credentials: "include" });
      if (!auRes.ok) {
        toast({ title: "Error", description: "Failed to load ad units." });
        return;
      }
      const rawAdUnits: any[] = await auRes.json();
      const adUnitMap = new Map(rawAdUnits.map((u: any) => [(u.name ?? "").toUpperCase().trim(), u]));

      const jobs = ads.map((ad, idx) => {
        const dbUnit = adUnitMap.get((ad.ad_unit ?? "").toUpperCase().trim());
        return {
          id: `${stageName}-${idx}`,
          adUnitName: ad.ad_unit,
          adConceptName: ad.ad_concept_name,
          adUnitId: dbUnit?.id ?? null,
          selectedAngle: ad.selected_angle ?? null,
          status: "idle" as const,
          imageUrl: null as string | null,
          error: null as string | null,
        };
      }).filter((j) => j.adUnitId);

      if (jobs.length === 0) {
        toast({ title: "No matching ad units", description: "Could not map any ads to your ad unit library. Check that unit names match." });
        return;
      }

      setStageImageJobs((prev) => ({
        ...prev,
        [stageName]: jobs.map((j) => ({ ...j, status: "idle" as const })),
      }));

      const angle = (a: string) => narrativeAngles.find((na) => na.name === a) ?? null;

      toast({ title: "Generating assets", description: `Starting ${jobs.length} image${jobs.length !== 1 ? "s" : ""} for ${stageName}...` });

      const STAGGER_MS = 2000;

      const genPromises = jobs.map((job, idx) => {
        return new Promise<void>((resolve) => {
          setTimeout(async () => {
            setStageImageJobs((prev) => ({
              ...prev,
              [stageName]: (prev[stageName] ?? []).map((j) =>
                j.id === job.id ? { ...j, status: "generating" as const } : j
              ),
            }));

            try {
              const res = await fetchCampaignImage("/api/generate-campaign-ad-image", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                  adUnitId: job.adUnitId,
                  product: product ?? null,
                  persona: persona ?? null,
                  narrativeAngle: angle(job.selectedAngle ?? ""),
                  narrativeOpportunity: narrativeOpportunity ?? null,
                  brand: brand ?? null,
                }),
              });

              if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error((errData as Record<string, string>).message ?? "Image generation failed");
              }

              const data = await res.json();
              setStageImageJobs((prev) => ({
                ...prev,
                [stageName]: (prev[stageName] ?? []).map((j) =>
                  j.id === job.id ? { ...j, status: "completed" as const, imageUrl: data.image_url, error: null } : j
                ),
              }));
            } catch (e: any) {
              const msg = e instanceof Error ? e.message : String(e);
              setStageImageJobs((prev) => ({
                ...prev,
                [stageName]: (prev[stageName] ?? []).map((j) =>
                  j.id === job.id ? { ...j, status: "failed" as const, error: msg } : j
                ),
              }));
            }
            resolve();
          }, idx * STAGGER_MS);
        });
      });

      await Promise.all(genPromises);
    } catch (err: any) {
      const msg = err instanceof Error ? err.message : String(err);
      toast({ title: "Asset generation failed", description: msg });
    }
  }, [inlineFunnelStages, stageAngleResults, stageAiResults, brand, product, persona, narrativeOpportunity, narrativeAngles, mediaAllocation, mediaMix, toast]);

  const handleFullPipeline = useCallback(async () => {
    if (!brand?.id || !inlineFunnelStages.length) return;
    setIsRunningFullPipeline(true);
    setFullPipelineStatus("Starting full pipeline...");

    const PHASE_STAGGER_MS = 2000;

    try {
      const auRes = await fetch(`/api/brands/${brand.id}/ad-units`, { credentials: "include" });
      if (!auRes.ok) throw new Error("Failed to load ad formats");
      const rawAdUnits: any[] = await auRes.json();
      if (!rawAdUnits.length) throw new Error("No ad formats found for this brand.");

      const mixErrors: string[] = [];
      const angleErrors: string[] = [];

      // ── PHASE 1: Generate diversity mix for ALL stages (staggered launch, parallel execution) ──
      setFullPipelineStatus(`Phase 1: Generating diversity mix for all ${inlineFunnelStages.length} stages...`);

      const mixPromises = inlineFunnelStages.map((stage, stageIdx) => {
        return new Promise<{ stageIdx: number; stageName: string; aiResult: StageAiResult | null }>(async (resolve) => {
          if (stageIdx > 0) await new Promise((r) => setTimeout(r, stageIdx * PHASE_STAGGER_MS));

          const stageName = stage.stage;
          setGeneratingStages((prev) => ({ ...prev, [stageIdx]: true }));

          try {
            const catalogGroup = stageToCatalogGroup(stage.stage, stage.stage_group ?? "");
            const stageSelectedIds = new Set(
              adUnitSelections
                .filter((s) => s.funnelStageId === catalogGroup || s.funnelStageId === stage.stage)
                .map((s) => s.adUnitId)
            );
            const planStagePlan = adUnitPlan?.stagePlans?.find(
              (sp) => sp.stage === stage.stage || sp.stage === catalogGroup
            );
            const planFormatNamesSet = new Set(
              planStagePlan?.ads?.map((a) => (a.ad_unit_name ?? "").toUpperCase().trim()) ?? []
            );
            let stageRawUnits: any[];
            let ads_to_select: number;

            // Mirror handleGeneratePlan exactly. The user's ad-unit selections
            // describe the POOL of formats the AI can pick from, NOT how many
            // ads to make. The production count comes from the campaign plan
            // allocation (or per-stage budget portion as fallback).
            //
            // Priority:
            //   1) AI per-stage allocation from the Campaign Plan.
            //   2) Per-stage portion of the user's static-units budget.
            //   3) Default of 3.
            const planStaticForStage = getPlanStaticForStage(
              mediaAllocation,
              stage.stage,
              stage.stage_group,
              stageIdx,
              mediaMix?.staticUnits ?? null,
            );
            const totalStages = Math.max(1, inlineFunnelStages.length);
            const wizardStaticUnits = mediaMix?.staticUnits ?? 0;
            const perStageStaticBudget = getStageBudgetPortion(wizardStaticUnits, stageIdx, totalStages);
            const allocationTarget = planStaticForStage > 0
              ? planStaticForStage
              : (perStageStaticBudget > 0 ? perStageStaticBudget : 3);

            const matchesStage = (u: any) => {
              const primary = (u.funnelStage || "").trim();
              const primaryLower = primary.toLowerCase();
              const secondary = (u.secondaryFunnel || "").trim();
              const unitCatalogGroup = stageToCatalogGroup(primary, "");
              return unitCatalogGroup === catalogGroup || primaryLower === stageName.toLowerCase() || secondary === stage.stage;
            };

            {
              const selectedUnits = stageSelectedIds.size > 0
                ? rawAdUnits.filter((u: any) => stageSelectedIds.has(u.id))
                : [];
              const stageMatchedUnits = rawAdUnits.filter(matchesStage);
              const planMatchedUnits = planFormatNamesSet.size > 0
                ? rawAdUnits.filter((u: any) => planFormatNamesSet.has((u.name ?? "").toUpperCase().trim()))
                : [];

              const seenIds = new Set<string>();
              stageRawUnits = [];
              for (const u of [...selectedUnits, ...stageMatchedUnits, ...planMatchedUnits]) {
                if (!seenIds.has(u.id)) {
                  seenIds.add(u.id);
                  stageRawUnits.push(u);
                }
              }

              // ads_to_select = how many ads to MAKE for this stage. Always
              // the allocation target (per-stage budget). The selected ad-units
              // are the POOL the AI picks from, not the count to produce.
              ads_to_select = allocationTarget;

              if (stageRawUnits.length < ads_to_select) {
                const adjacentGroups: Record<string, string[]> = { tof: ["mof"], mof: ["tof", "bof"], bof: ["mof"] };
                const expandTo = adjacentGroups[catalogGroup] ?? [];
                for (const adj of expandTo) {
                  const extra = rawAdUnits.filter(
                    (u: any) => stageToCatalogGroup((u.funnelStage || "").trim(), "") === adj && !seenIds.has(u.id)
                  );
                  for (const u of extra) { stageRawUnits.push(u); seenIds.add(u.id); }
                  if (stageRawUnits.length >= ads_to_select) break;
                }
              }
            }

            if (!stageRawUnits.length) {
              setGeneratingStages((prev) => { const next = { ...prev }; delete next[stageIdx]; return next; });
              resolve({ stageIdx, stageName, aiResult: null });
              return;
            }

            const available_ad_units = stageRawUnits.map((u: any) => ({
              id: u.id, name: u.name, format: u.formatType ?? "static",
              funnel_stage: u.funnelStage ?? "tof", objective: u.objective ?? "",
              strategic_role: (u.strategicRole ?? "").slice(0, 300),
            }));

            // user_ad_unit: intentionally empty. Selections are the POOL,
            // not forced picks. See the matching comment in handleGeneratePlan.
            const user_ad_unit: Array<Record<string, unknown>> = [];

            const payload = {
              product: product ? { name: product.name, description: (product as any).description ?? (product as any).positioning ?? product.name, category: (product as any).category ?? "" } : { name: "Product", description: "" },
              persona: persona ? { name: persona.name, summary: (plannerPersona as any)?.summary ?? (persona as any).bio ?? persona.name, age_range: (persona as any).ageRange ?? "", primary_pain: (plannerPersona as any)?.primary_pain ?? "" } : { name: "Persona", summary: "" },
              brand: { name: brand.name, positioning: (brand as any).positioning ?? (brand as any).brandVoice ?? brand.name },
              narrative_opportunity: narrativeOpportunity ? { title: narrativeOpportunity.title, summary: (narrativeOpportunity as any).summary ?? narrativeOpportunity.title } : { title: "Campaign", summary: "" },
              selected_narrative_angles: narrativeAngles.slice(0, 5).map((a) => ({ id: a.id, title: a.name, hook: (a as any).hook ?? a.name })),
              funnel_stage: { stage: stage.stage, stage_group: stage.stage_group ?? catalogGroup, objective: stage.objective ?? "", psychological_task: stage.psychological_task ?? "", messaging_focus: stage.messaging_focus ?? "", emotional_job: stage.emotional_job ?? "", message_style: stage.message_style ?? "" },
              available_ad_units,
              ads_to_select,
              user_ad_unit,
              creative_strategist: creativeStrategist ? { name: creativeStrategist.name, archetype: creativeStrategist.archetype, mission: creativeStrategist.mission, strategic_mindset: creativeStrategist.strategic_mindset, signature_strength: creativeStrategist.signature_strength, creative_philosophy: creativeStrategist.creative_philosophy, audience_and_funnel_instincts: creativeStrategist.audience_and_funnel_instincts, decision_framework: creativeStrategist.decision_framework } : null,
            };

            const mixRes = await fetch("/api/generate-stage-ad-selection", { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(payload) });
            if (!mixRes.ok) {
              const err = await mixRes.json().catch(() => ({}));
              throw new Error((err as any).message ?? `Server returned ${mixRes.status}`);
            }
            const mixResult = await mixRes.json();
            const aiResult: StageAiResult = { executive_summary: mixResult?.executive_summary ?? "", selected_ad_units: mixResult?.selected_ad_units ?? [] };
            setStageAiResults((prev) => ({ ...prev, [stageName]: aiResult }));
            setGeneratingStages((prev) => { const next = { ...prev }; delete next[stageIdx]; return next; });
            resolve({ stageIdx, stageName, aiResult });
          } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : String(e);
            mixErrors.push(`${stage.stage}: ${msg}`);
            setGeneratingStages((prev) => { const next = { ...prev }; delete next[stageIdx]; return next; });
            resolve({ stageIdx, stageName: stage.stage, aiResult: null });
          }
        });
      });

      const mixResults = await Promise.all(mixPromises);
      const successfulMixes = mixResults.filter((r) => r.aiResult !== null);

      if (mixErrors.length > 0) {
        toast({ title: `Diversity mix failed for ${mixErrors.length} stage(s)`, description: mixErrors.join("; "), variant: "destructive" });
      }

      if (successfulMixes.length === 0) {
        throw new Error("All diversity mix generations failed.");
      }

      // ── PHASE 2: Assign angles for ALL successful stages (staggered launch, parallel execution) ──
      setFullPipelineStatus(`Phase 2: Assigning angles for ${successfulMixes.length} stages...`);

      const anglePromises = successfulMixes.map((mix, i) => {
        return new Promise<{ stageIdx: number; stageName: string; angleResult: any | null }>(async (resolve) => {
          if (i > 0) await new Promise((r) => setTimeout(r, i * PHASE_STAGGER_MS));

          const { stageIdx, stageName, aiResult } = mix;
          setAssigningAnglesStages((prev) => ({ ...prev, [stageIdx]: true }));

          try {
            const dedupedMap = new Map<string, number>();
            for (const u of aiResult!.selected_ad_units) {
              dedupedMap.set(u.ad_unit_name, (dedupedMap.get(u.ad_unit_name) ?? 0) + (u.quantity ?? 1));
            }
            const ad_units = Array.from(dedupedMap.entries()).map(([name, qty]) => ({ ad_unit_name: name, quantity: qty }));

            const angleRes = await fetch("/api/assign-angles", {
              method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
              body: JSON.stringify({ product, persona, narrative_opportunity: narrativeOpportunity, narrative_angles: narrativeAngles, funnel_stage: stageName, funnel_strategy: funnelStrategy, ad_units }),
            });
            if (!angleRes.ok) {
              const err = await angleRes.json().catch(() => ({}));
              throw new Error((err as any).message ?? `Server returned ${angleRes.status}`);
            }
            const angleResult = await angleRes.json();
            setStageAngleResults((prev) => ({ ...prev, [stageName]: angleResult }));
            setAssigningAnglesStages((prev) => { const next = { ...prev }; delete next[stageIdx]; return next; });
            resolve({ stageIdx, stageName, angleResult });
          } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : String(e);
            angleErrors.push(`${stageName}: ${msg}`);
            setAssigningAnglesStages((prev) => { const next = { ...prev }; delete next[stageIdx]; return next; });
            resolve({ stageIdx, stageName, angleResult: null });
          }
        });
      });

      const angleResults = await Promise.all(anglePromises);

      if (angleErrors.length > 0) {
        toast({ title: `Angle assignment failed for ${angleErrors.length} stage(s)`, description: angleErrors.join("; "), variant: "destructive" });
      }

      // ── PHASE 3: Generate assets sequentially for all stages with angles ──
      const stagesWithAngles = angleResults.filter((r) => r.angleResult !== null);
      const adUnitMap = new Map(rawAdUnits.map((u: any) => [(u.name ?? "").toUpperCase().trim(), u]));
      const angle = (a: string) => narrativeAngles.find((na) => na.name === a) ?? null;
      let totalImages = 0;
      let completedImages = 0;
      let failedImages = 0;

      const allJobs: Array<{ stageName: string; job: any }> = [];

      for (const { stageName, angleResult } of stagesWithAngles) {
        const stageAi = mixResults.find((r) => r.stageName === stageName)?.aiResult;

        // Compute the planned target for this stage so we never make MORE ads
        // than the badge promises. Mirrors handleGeneratePlan / badge logic.
        const stageObjForTarget = inlineFunnelStages.find((s) => s.stage === stageName);
        const stageIdxForTarget = inlineFunnelStages.findIndex((s) => s.stage === stageName);
        const planStaticForStage = getPlanStaticForStage(
          mediaAllocation,
          stageName,
          stageObjForTarget?.stage_group,
          stageIdxForTarget,
          mediaMix?.staticUnits ?? null,
        );
        const totalStagesForTarget = Math.max(1, inlineFunnelStages.length);
        const wizardStaticUnits = mediaMix?.staticUnits ?? 0;
        const perStageStaticBudget = getStageBudgetPortion(wizardStaticUnits, stageIdxForTarget, totalStagesForTarget);
        const targetCount = planStaticForStage > 0
          ? planStaticForStage
          : (perStageStaticBudget > 0 ? perStageStaticBudget : 3);

        // Build asset list from the AI mix expanded by quantity (one job per
        // slot in the Prescribed Mix), pulling angles from angleResult by
        // ad_unit name + slot index. Falls back to the angle list directly if
        // we don't have an aiResult to size against.
        const ads: Array<{ ad_concept_name: string; ad_unit: string; selected_angle: string }> = [];
        const anglesByUnit = new Map<string, any[]>();
        if (angleResult?.ad_unit_selections?.length) {
          for (const a of angleResult.ad_unit_selections) {
            const key = a.ad_unit;
            if (!anglesByUnit.has(key)) anglesByUnit.set(key, []);
            anglesByUnit.get(key)!.push(a);
          }
        }
        if (stageAi?.selected_ad_units?.length) {
          const dedupedMap = new Map<string, number>();
          for (const u of stageAi.selected_ad_units) {
            dedupedMap.set(u.ad_unit_name, (dedupedMap.get(u.ad_unit_name) ?? 0) + ((u as any).quantity ?? 1));
          }
          for (const [name, qty] of dedupedMap.entries()) {
            const unitAngles = anglesByUnit.get(name) ?? [];
            for (let slot = 0; slot < qty; slot++) {
              const assigned = unitAngles[slot] ?? null;
              ads.push({
                ad_concept_name: assigned?.ad_concept_name ?? `${name} Ad ${slot + 1}`,
                ad_unit: name,
                selected_angle: assigned?.selected_angle ?? "",
              });
            }
          }
        } else if (angleResult?.ad_unit_selections?.length) {
          for (const sel of angleResult.ad_unit_selections) {
            ads.push({ ad_concept_name: sel.ad_concept_name, ad_unit: sel.ad_unit, selected_angle: sel.selected_angle });
          }
        }

        // Clamp to planned target so the per-stage badge and the actual jobs
        // created always agree.
        if (ads.length > targetCount) {
          ads.length = targetCount;
        }

        const jobs = ads.map((ad, idx) => {
          const dbUnit = adUnitMap.get((ad.ad_unit ?? "").toUpperCase().trim());
          return {
            id: `${stageName}-${idx}`,
            adUnitName: ad.ad_unit,
            adConceptName: ad.ad_concept_name,
            adUnitId: dbUnit?.id ?? null,
            selectedAngle: ad.selected_angle ?? null,
            status: "idle" as const,
            imageUrl: null as string | null,
            error: null as string | null,
          };
        }).filter((j) => j.adUnitId);

        if (jobs.length > 0) {
          setStageImageJobs((prev) => ({ ...prev, [stageName]: jobs.map((j) => ({ ...j, status: "idle" as const })) }));
          for (const job of jobs) allJobs.push({ stageName, job });
        }
        setSavedStages((prev) => ({ ...prev, [stageName]: true }));
      }

      totalImages = allJobs.length;

      if (totalImages > 0) {
        // Render every static asset through the SAME modern engine the per-ad
        // "Generate" button and Ads Express use: /api/generate-campaign-ad-image
        // → generateAdImage (the "New NB Prompt" workflow + Ads Express
        // reference handling). The previous two-phase path (legacy 82bb1dfb
        // prompt workflow + /api/generate-fal-image) produced lower-quality
        // copy (typos / garbled statements) that Ads Express never had.
        setFullPipelineStatus(`Rendering ${totalImages} asset${totalImages !== 1 ? "s" : ""}...`);

        for (const { stageName, job } of allJobs) {
          setStageImageJobs((prev) => ({
            ...prev,
            [stageName]: (prev[stageName] ?? []).map((j) => j.id === job.id ? { ...j, status: "generating" as const, error: null } : j),
          }));
        }

        const generateOne = async ({ stageName, job }: (typeof allJobs)[number]) => {
          const MAX_RETRIES = 1;
          for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
            try {
              if (attempt > 0) {
                setStageImageJobs((prev) => ({
                  ...prev,
                  [stageName]: (prev[stageName] ?? []).map((j) => j.id === job.id ? { ...j, status: "generating" as const, error: null } : j),
                }));
              }
        const res = await fetchCampaignImage("/api/generate-campaign-ad-image", {
                method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
                body: JSON.stringify({ adUnitId: job.adUnitId, product: product ?? null, persona: persona ?? null, narrativeAngle: angle(job.selectedAngle ?? ""), narrativeOpportunity: narrativeOpportunity ?? null, brand: brand ?? null }),
              });
              if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error((errData as Record<string, string>).message ?? "Image generation failed");
              }
              const data = await res.json();
              completedImages++;
              setFullPipelineStatus(`${completedImages}/${totalImages} assets done...`);
              setStageImageJobs((prev) => ({
                ...prev,
                [stageName]: (prev[stageName] ?? []).map((j) => j.id === job.id ? { ...j, status: "completed" as const, imageUrl: data.image_url, error: null } : j),
              }));
              return;
            } catch (e: any) {
              const msg = e instanceof Error ? e.message : String(e);
              const isTimeout = msg.toLowerCase().includes("timed out");
              if (isTimeout && attempt < MAX_RETRIES) {
                console.log(`[Pipeline] Image timed out for ${stageName}, retrying (attempt ${attempt + 1})...`);
                continue;
              }
              failedImages++;
              setStageImageJobs((prev) => ({
                ...prev,
                [stageName]: (prev[stageName] ?? []).map((j) => j.id === job.id ? { ...j, status: "failed" as const, error: msg } : j),
              }));
              return;
            }
          }
        };

        // Throttle to MAX_CONCURRENT in-flight jobs at once. Without this,
        // firing 30+ image jobs in parallel triggers fal.ai's "Forbidden" rate
        // limit on every single one. With a small concurrency the per-call retries
        // never need to fire and every image actually completes.
        const MAX_CONCURRENT = 3;
        let nextIdx = 0;
        const worker = async () => {
          while (true) {
            const myIdx = nextIdx++;
            if (myIdx >= allJobs.length) return;
            await generateOne(allJobs[myIdx]);
          }
        };
        await Promise.all(Array.from({ length: Math.min(MAX_CONCURRENT, allJobs.length) }, worker));
      }

      const hasErrors = mixErrors.length > 0 || angleErrors.length > 0 || failedImages > 0;
      const statusMsg = hasErrors
        ? `Done — ${successfulMixes.length} mixes, ${stagesWithAngles.length} angles, ${completedImages}/${totalImages} assets`
        : "Pipeline complete!";
      setFullPipelineStatus(statusMsg);
      toast({
        title: hasErrors ? "Pipeline finished with some errors" : "Full pipeline complete",
        description: `${successfulMixes.length} mixes, ${stagesWithAngles.length} angle sets, ${completedImages} asset${completedImages !== 1 ? "s" : ""} generated.`,
        variant: hasErrors ? "destructive" : "default",
      });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Pipeline failed", description: msg, variant: "destructive" });
    } finally {
      setIsRunningFullPipeline(false);
      setTimeout(() => setFullPipelineStatus(null), 5000);
    }
  }, [brand, inlineFunnelStages, adUnitSelections, adUnitPlan, mediaAllocation, mediaMix, product, persona, plannerPersona, narrativeOpportunity, narrativeAngles, funnelStrategy, creativeStrategist, toast]);

  // Product packaging/reference images, used to keep the product faithful when
  // editing a generated static ad (mirrors the server's generation preference:
  // adgenImageRefs first, falling back to mediaJson).
  const productRefImageUrls = useMemo<string[]>(() => {
    const p = product as any;
    if (!p) return [];
    const adgen = Array.isArray(p.adgenImageRefs)
      ? p.adgenImageRefs.filter((r: unknown): r is string => typeof r === "string" && r.length > 0)
      : [];
    if (adgen.length > 0) return adgen;
    return Array.isArray(p.mediaJson)
      ? p.mediaJson.filter((r: unknown): r is string => typeof r === "string" && r.length > 0)
      : [];
  }, [product]);

  const counts = computeCounts(localQueue);
  const stageGroups = groupByStage(localQueue);
  const stages = Array.from(stageGroups.keys());
  const currentRunningJob = localQueue.find((j) => j.status === "running") ?? null;
  const hasQueue = localQueue.length > 0;
  const canResume =
    !isProcessing &&
    hasQueue &&
    localQueue.some((j) => j.status === "pending" || j.status === "failed");
  const progressPercent =
    counts.total > 0 ? Math.round((counts.completed / counts.total) * 100) : 0;

  const imageCounts = useMemo(() => {
    const staticJobs = localQueue.filter(
      (j) => j.format === "static" && (j.status === "completed" || j.status === "locked")
    );
    return {
      total: staticJobs.length,
      completed: staticJobs.filter((j) => j.imageStatus === "completed").length,
      generating: staticJobs.filter((j) => j.imageStatus === "generating").length,
      failed: staticJobs.filter((j) => j.imageStatus === "failed").length,
      idle: staticJobs.filter((j) => !j.imageStatus || j.imageStatus === "idle" || j.imageStatus === "failed").length,
    };
  }, [localQueue]);
  const displayStage = activeStage ?? stages[0] ?? null;

  const saveHandlerRef = useRef<() => Promise<void>>(() => Promise.resolve());

  const pipelineHasCompleted = useMemo(() => {
    return Object.values(stageImageJobs).some((jobs) =>
      jobs.some((j) => j.status === "completed" && j.imageUrl)
    );
  }, [stageImageJobs]);

  const pipelineIsGenerating = isRunningFullPipeline;

  useEffect(() => {
    const hasCompletedWork = counts.completed > 0 || pipelineHasCompleted;
    const canSave = hasCompletedWork && !isSaving && !isProcessing && !pipelineIsGenerating;
    onRegisterSave?.({
      save: () => saveHandlerRef.current(),
      canSave,
      isSaving,
    });
  }, [counts.completed, counts.total, isSaving, isProcessing, hasQueue, pipelineHasCompleted, pipelineIsGenerating, onRegisterSave]);

  // Derived display flags for inline stage selector
  const showInlineStageSelector = !!creativeStrategist && inlineTotalStages > 0 && !!brand?.id;
  const showGenerateBriefsCard = !!creativeStrategist && inlineTotalStages === 0;

  const handleSaveCampaign = async () => {
    if (!brand?.id) {
      toast({ title: "Error", description: "No brand context found", variant: "destructive" });
      return;
    }
    setIsSaving(true);
    try {
      const campaignId = draftId.startsWith("draft-") ? "new" : draftId;
      const res = await fetch(`/api/campaigns/${campaignId}/finalize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          brandId: brand.id,
          campaignName: draftName && draftName !== "Untitled Campaign"
            ? draftName
            : [persona?.name, product?.name, narrativeOpportunity?.title].filter(Boolean).join(" x ") || "Campaign",
          campaignType: new URLSearchParams(window.location.search).get("type") || "standard",
          persona: persona ?? null,
          product: product ?? null,
          narrative: narrativeOpportunity ?? null,
          creatives: localQueue.filter((j) => j.status === "completed" || j.generatedImageUrl),
          stageImageJobs: stageImageJobs,
          funnelStrategy: funnelStrategy ?? null,
          campaignPlan: campaignPlan ?? null,
          mediaAllocation: mediaAllocation ?? null,
          adUnitSelections: adUnitSelections ?? null,
          adUnitPlan: adUnitPlan ?? null,
          narrativeAngles: narrativeAngles ?? null,
          plannerPersona: plannerPersona ?? null,
          creativeStrategist: creativeStrategist ?? null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Save failed" }));
        throw new Error(err.message || "Save failed");
      }
      const data = await res.json();
      toast({
        title: "Campaign saved",
        description: `${data.savedCount} creative${data.savedCount !== 1 ? "s" : ""} saved to campaign.`,
      });
      onResetDraft?.();
      const isUgc = new URLSearchParams(window.location.search).get("type") === "ugc";
      navigate(`/brands/${brand.id}/campaigns/${data.campaignId}`);
    } catch (err: any) {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };
  saveHandlerRef.current = handleSaveCampaign;

  if (!hasQueue && !isProcessing) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Creative Briefs</h2>
          <p className="text-muted-foreground mt-1">
            Generate execution-ready briefs for every ad in the campaign
          </p>
        </div>

        {isStale && localQueue.length > 0 && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-yellow-500/30 bg-yellow-500/5 text-yellow-600 dark:text-yellow-400">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <p className="text-sm">Upstream selections changed. Review or regenerate your briefs.</p>
          </div>
        )}

        <CreativeStrategistSection
          strategist={creativeStrategist}
          isGenerating={isGeneratingStrategist}
          error={strategistError}
          onGenerate={handleGenerateStrategist}
          onRegenerate={() => {
            // Regenerating the AI Creative Strategist invalidates everything
            // downstream that was generated under the old strategist's
            // direction (per-stage diversity mix, angle assignments, saved
            // stages, generated images, and the brief queue). Wipe it all so
            // the user must re-run the diversity mix with the new strategist.
            onUpdateCreativeStrategist(null);
            clearDiversityMix();
            handleGenerateStrategist();
          }}
        />

        {showInlineStageSelector && (
          <div className="space-y-3">
            <Card className="border border-violet-500/20 bg-gradient-to-r from-violet-500/5 to-purple-500/5">
              <div className="px-5 py-4 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-violet-500/10 flex items-center justify-center">
                      <Zap className="w-4.5 h-4.5 text-violet-500" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold" data-testid="text-full-pipeline-title">Generate Full Campaign</p>
                      <p className="text-xs text-muted-foreground">Diversity mix, angle assignment, and asset generation for all stages</p>
                    </div>
                  </div>
                  <Button
                    data-testid="button-full-pipeline"
                    onClick={handleFullPipeline}
                    disabled={isRunningFullPipeline || !brand?.id}
                    className="rounded-full bg-violet-600 hover:bg-violet-700 text-white gap-2 px-5"
                  >
                    {isRunningFullPipeline
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Running...</>
                      : <><Zap className="w-3.5 h-3.5" />Generate All</>}
                  </Button>
                </div>
                {fullPipelineStatus && (
                  <div className="flex items-center gap-2 text-xs text-violet-600 dark:text-violet-400">
                    {isRunningFullPipeline && <Loader2 className="w-3 h-3 animate-spin" />}
                    {!isRunningFullPipeline && <CheckCircle2 className="w-3 h-3" />}
                    <span data-testid="text-pipeline-status">{fullPipelineStatus}</span>
                  </div>
                )}
              </div>
            </Card>

            <div className="flex items-center justify-between px-1">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Diversity Mix
              </p>
              {(Object.keys(stageAiResults).length > 0 ||
                Object.keys(stageAngleResults).length > 0 ||
                Object.keys(savedStages).length > 0 ||
                Object.keys(stageImageJobs).length > 0 ||
                localQueue.length > 0) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (
                      window.confirm(
                        "Reset the entire diversity mix? This will clear all stage selections, angle assignments, generated images, and briefs on this page. Upstream campaign data is preserved."
                      )
                    ) {
                      clearDiversityMix();
                      toast({
                        title: "Diversity mix reset",
                        description: "Re-run the diversity mix for each stage to rebuild your briefs.",
                      });
                    }
                  }}
                  className="rounded-full text-muted-foreground hover:text-destructive h-7 px-3 text-xs gap-1"
                  data-testid="btn-reset-diversity-mix"
                >
                  <RotateCcw className="w-3 h-3" />
                  Reset Diversity Mix
                </Button>
              )}
            </div>
            {inlineFunnelStages.map((fs, i) => {
              const prevStage = i > 0 ? inlineFunnelStages[i - 1] : null;
              const isLocked = false;
              return (
                <InlineAdUnitStageSelector
                  key={i}
                  funnelStages={inlineFunnelStages}
                  adUnitSelections={adUnitSelections}
                  adUnitPlan={adUnitPlan}
                  stageIndex={i}
                  mediaAllocation={mediaAllocation}
                  mediaMix={mediaMix}
                  onGenerate={() => handleGeneratePlan(i)}
                  onAssignAngles={() => handleAssignAngles(i)}
                  onSaveStage={() => handleSaveStage(i)}
                  onGenerateStageAssets={() => handleGenerateStageAssets(i)}
                  isGeneratingPlan={!!generatingStages[i]}
                  isAssigningAngles={!!assigningAnglesStages[i]}
                  isLocked={isLocked}
                  isSaved={!!savedStages[fs.stage]}
                  aiResult={stageAiResults[fs.stage] ?? null}
                  angleResult={stageAngleResults[fs.stage] ?? null}
                  stageImageCounts={(() => {
                    const jobs = stageImageJobs[fs.stage];
                    if (!jobs?.length) return null;
                    return {
                      total: jobs.length,
                      generating: jobs.filter((j) => j.status === "generating").length,
                      completed: jobs.filter((j) => j.status === "completed").length,
                      failed: jobs.filter((j) => j.status === "failed").length,
                      idle: jobs.filter((j) => j.status === "idle").length,
                    };
                  })()}
                  stageImageJobs={stageImageJobs[fs.stage] ?? null}
                  availableFormatNames={stageAvailableFormats[fs.stage] ?? []}
                  onUpdateStageImageUrl={(jobId, newUrl) => {
                    setStageImageJobs((prev) => {
                      const jobs = prev[fs.stage];
                      if (!jobs) return prev;
                      return {
                        ...prev,
                        [fs.stage]: jobs.map((j) =>
                          j.id === jobId ? { ...j, imageUrl: newUrl } : j
                        ),
                      };
                    });
                  }}
                  referenceImageUrls={productRefImageUrls}
                  brandId={brand?.id}
                />
              );
            })}
          </div>
        )}

        {showGenerateBriefsCard && (
          <Card className="border border-border">
            <div className="pt-12 pb-12 flex flex-col items-center text-center gap-6 px-6">
              <div className="w-16 h-16 rounded-2xl bg-violet-500/10 flex items-center justify-center">
                <FileText className="w-8 h-8 text-violet-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold">Generate your creative briefs</h3>
                <p className="text-muted-foreground mt-2 max-w-md mx-auto">
                  Turn every planned ad into an execution-ready brief with hook direction, visual
                  structure, and copy guidance.
                </p>
              </div>

              {adUnitSelections.length > 0 ? (
                <div className="flex gap-3 flex-wrap justify-center items-center">
                  {inlineFunnelStages.map((fs, i) => {
                    const count = adUnitSelections.filter((s) => s.funnelStageId === fs.stage).length;
                    if (count === 0) return null;
                    const Icon = STAGE_ICONS[i % STAGE_ICONS.length];
                    return (
                      <Badge key={fs.stage} variant="outline" className="text-sm px-3 py-1 gap-1.5">
                        <Icon className="h-3 w-3" />
                        {count} {fs.stage}
                      </Badge>
                    );
                  })}
                  <button
                    className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                    data-testid="btn-edit-ad-selection"
                  >
                    Edit
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <p className="text-sm text-muted-foreground">No ad formats selected yet.</p>
                  <button
                    className="text-xs text-primary hover:underline underline-offset-2"
                    data-testid="btn-select-ad-formats"
                  >
                    Select ad formats
                  </button>
                </div>
              )}

              <Button
                className="rounded-full px-8 bg-violet-600 hover:bg-violet-700 text-white"
                onClick={handleGenerate}
                disabled={adUnitSelections.length === 0}
                data-testid="btn-generate-briefs"
              >
                Generate Briefs
              </Button>
            </div>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Creative Briefs</h2>
          <p className="text-muted-foreground mt-1">
            Generate execution-ready briefs for every ad in the campaign
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {isProcessing ? (
            <Button
              variant="outline"
              className="rounded-full"
              onClick={handlePause}
              data-testid="btn-pause-generation"
            >
              <Pause className="w-4 h-4 mr-2" />
              Pause
            </Button>
          ) : (
            <>
              {canResume && (
                <Button
                  className="rounded-full bg-violet-600 hover:bg-violet-700 text-white"
                  onClick={handleResume}
                  data-testid="btn-resume-generation"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Resume
                </Button>
              )}
              {counts.failed > 0 && (
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={handleRetryFailed}
                  data-testid="btn-retry-failed"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry Failed ({counts.failed})
                </Button>
              )}
              <Button
                variant="outline"
                className="rounded-full"
                onClick={handleRegenerateAll}
                disabled={isProcessing}
                data-testid="btn-regenerate-all"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Regenerate All
              </Button>
            </>
          )}
        </div>
      </div>

      {isStale && localQueue.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-yellow-500/30 bg-yellow-500/5 text-yellow-600 dark:text-yellow-400">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <p className="text-sm">Upstream selections changed. Review or regenerate your briefs.</p>
        </div>
      )}

      <CreativeStrategistSection
        strategist={creativeStrategist}
        isGenerating={isGeneratingStrategist}
        error={strategistError}
        onGenerate={handleGenerateStrategist}
        onRegenerate={() => {
          // Same invalidation as the inline strategist section: regenerating
          // wipes everything generated under the old strategist (per-stage
          // diversity mix, angle assignments, saved stages, generated images,
          // queue, and the per-stage media allocation that drives the
          // "X statics planned" badge).
          onUpdateCreativeStrategist(null);
          clearDiversityMix();
          handleGenerateStrategist();
        }}
      />

      <Card className="border border-border">
        <div className="px-5 pt-5 pb-5">
          <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
            <div className="flex items-center gap-6 flex-wrap">
              <div className="text-center">
                <p className="text-2xl font-bold">{counts.total}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Total</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-500">{counts.completed}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Done</p>
              </div>
              {counts.running > 0 && (
                <div className="text-center">
                  <p className="text-2xl font-bold text-violet-400">{counts.running}</p>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Active</p>
                </div>
              )}
              {counts.failed > 0 && (
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-500">{counts.failed}</p>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Failed</p>
                </div>
              )}
              <div className="text-center">
                <p className="text-2xl font-bold text-muted-foreground">{counts.pending}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Pending</p>
              </div>
            </div>

            <div className="text-right">
              <p className="text-sm font-semibold">
                {counts.completed} / {counts.total}
              </p>
              <p className="text-xs text-muted-foreground">
                {isProcessing
                  ? `Generating ${currentRunningJob?.stage ?? ""} briefs`
                  : isPaused
                    ? "Paused"
                    : counts.completed === counts.total && counts.total > 0
                      ? "Complete"
                      : counts.failed > 0
                        ? `${counts.failed} failed`
                        : "Ready"}
              </p>
            </div>
          </div>

          <Progress value={progressPercent} className="h-2" data-testid="progress-briefs" />
        </div>
      </Card>

      {stages.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {stages.map((stage) => {
            const stageJobs = stageGroups.get(stage) ?? [];
            const stageCompleted = stageJobs.filter(
              (j) => j.status === "completed" || j.status === "locked"
            ).length;
            const stageFailed = stageJobs.filter((j) => j.status === "failed").length;
            const stageRunning = stageJobs.some((j) => j.status === "running");
            const isActive = displayStage === stage;

            return (
              <button
                key={stage}
                onClick={() => setActiveStage(stage)}
                data-testid={`stage-tab-${stage.toLowerCase()}`}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border transition-all duration-150 ${
                  isActive
                    ? "bg-violet-600 text-white border-violet-600"
                    : "bg-background border-border hover:border-violet-400 text-muted-foreground hover:text-foreground"
                }`}
              >
                {stageRunning && <Loader2 className="w-3 h-3 animate-spin" />}
                {!stageRunning && stageFailed > 0 && stageCompleted < stageJobs.length && (
                  <XCircle className="w-3 h-3 text-red-400" />
                )}
                {!stageRunning && stageCompleted === stageJobs.length && stageJobs.length > 0 && (
                  <CheckCircle className="w-3 h-3 text-emerald-400" />
                )}
                <span>{stage}</span>
                <span
                  className={`text-xs ${isActive ? "text-white/70" : "text-muted-foreground"}`}
                >
                  {stageCompleted}/{stageJobs.length}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {isProcessing && currentRunningJob && (
        <LiveGenerationPanel job={currentRunningJob} statusMessage={liveStatusMsg} />
      )}

      {displayStage && stageGroups.has(displayStage) && (() => {
        const stageJobs = stageGroups.get(displayStage) ?? [];
        const stageStaticJobs = stageJobs.filter(
          (j) => j.format === "static" && (j.status === "completed" || j.status === "locked")
        );
        const stageImgCompleted = stageStaticJobs.filter((j) => j.imageStatus === "completed").length;
        const stageImgGenerating = stageStaticJobs.filter((j) => j.imageStatus === "generating").length;
        const stageImgFailed = stageStaticJobs.filter((j) => j.imageStatus === "failed").length;
        const stageImgTotal = stageStaticJobs.length;
        const hasImageActivity = stageImgGenerating > 0 || stageImgCompleted > 0 || stageImgFailed > 0;

        return (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {displayStage} Briefs
            </h3>

            {stageImgTotal > 0 && hasImageActivity && (
              <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-border bg-muted/30">
                <Image className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium">
                      {stageImgGenerating > 0
                        ? `Generating images (${stageImgCompleted}/${stageImgTotal})`
                        : `Images ${stageImgCompleted}/${stageImgTotal}`}
                    </span>
                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                      {stageImgGenerating > 0 && (
                        <span className="text-violet-500 flex items-center gap-1">
                          <Loader2 className="w-2.5 h-2.5 animate-spin" />{stageImgGenerating} active
                        </span>
                      )}
                      {stageImgFailed > 0 && (
                        <span className="text-red-500">{stageImgFailed} failed</span>
                      )}
                    </div>
                  </div>
                  <Progress
                    value={stageImgTotal > 0 ? Math.round((stageImgCompleted / stageImgTotal) * 100) : 0}
                    className="h-1.5"
                    data-testid={`progress-stage-images-${displayStage}`}
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              {stageJobs.map((job) => (
                <BriefCard
                  key={job.jobId}
                  job={job}
                  onRegenerate={handleRegenerate}
                  onRetry={handleRetry}
                  onToggleLock={handleToggleLock}
                  onGenerateImage={handleGenerateImage}
                  onUpdateImageUrl={handleUpdateImageUrl}
                  isGenerating={isProcessing}
                  referenceImageUrls={productRefImageUrls}
                  brandId={brand?.id}
                />
              ))}
            </div>
          </div>
        );
      })()}

      {counts.completed > 0 && imageCounts.total > 0 && (
        <Card className="border border-border">
          <div className="px-5 py-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-5">
                <div className="flex items-center gap-2">
                  <Image className="w-4 h-4 text-violet-500" />
                  <span className="text-sm font-semibold">Asset Generation</span>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>{imageCounts.total} eligible</span>
                  {imageCounts.completed > 0 && (
                    <span className="text-emerald-500 font-medium">{imageCounts.completed} done</span>
                  )}
                  {imageCounts.generating > 0 && (
                    <span className="text-violet-500 font-medium flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      {imageCounts.generating} generating
                    </span>
                  )}
                  {imageCounts.failed > 0 && (
                    <span className="text-red-500 font-medium">{imageCounts.failed} failed</span>
                  )}
                </div>
              </div>
              <Button
                className="rounded-full px-6 bg-violet-600 hover:bg-violet-700 text-white"
                onClick={handleGenerateAllImages}
                disabled={isProcessing || imageCounts.idle === 0}
                data-testid="btn-generate-assets"
              >
                {imageCounts.generating > 0 ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                ) : imageCounts.completed === imageCounts.total && imageCounts.total > 0 ? (
                  <><RefreshCw className="w-4 h-4 mr-2" />Regenerate Assets</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" />Generate Assets ({imageCounts.idle})</>
                )}
              </Button>
            </div>
            {imageCounts.total > 0 && (
              <Progress
                value={imageCounts.total > 0 ? Math.round(((imageCounts.completed) / imageCounts.total) * 100) : 0}
                className="h-1.5 mt-3"
                data-testid="progress-assets"
              />
            )}
          </div>
        </Card>
      )}

      {counts.completed > 0 && (
        <div className="flex justify-end gap-3 pt-2">
          {creativeBriefs.status !== "generated" && (
            <Button
              className="rounded-full px-8 bg-violet-600 hover:bg-violet-700 text-white"
              onClick={() => {
                persistQueue(localQueue, "generated");
                onClearStale();
                toast({
                  title: "Briefs approved",
                  description: "All completed briefs are locked in.",
                });
              }}
              disabled={isProcessing}
              data-testid="btn-approve-briefs"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Approve Briefs
            </Button>
          )}
          <Button
            className="rounded-full px-8 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={handleSaveCampaign}
            disabled={isSaving || isProcessing}
            data-testid="btn-save-campaign"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isSaving ? "Saving..." : "Save Campaign"}
          </Button>
        </div>
      )}
    </div>
  );
}
